import React, { useState, useEffect, useRef, useMemo, FC } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import { Card, Row, Col } from "reactstrap";
import {
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../../components/Component";
import {
  Table,
  Tabs,
  Tag,
  Select,
  Progress,
  DatePicker,
  Alert,
  Spin,
} from "antd";
import {
  ArrowUpOutlined,
  DollarOutlined,
  BarChartOutlined,
  PieChartOutlined,
  CheckCircleOutlined,
  FundProjectionScreenOutlined,
  AlertOutlined,
  FilterOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import * as echarts from "echarts";
import { formatCurrency } from "../../../utils/Utils";
import budgetAnalysisDashboardApis from "../../../api/master/budgetAnalysisDashboard";
import budgetApis from "../../../api/master/budget";
import contractorsApis from "../../../api/master/contractors";
import { projectsApis } from "../../../api/projects";
import { updateMenuData } from "../../../store/slices/misc/menuData";
import TopMenu from "../../../route/topMenu";

const { Option } = Select;
const { RangePicker } = DatePicker;

// Helper to format Cr/L
const formatCr = (v: number) => {
  if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`;
  if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  return formatCurrency(v, "INR");
};

// Accordion Table: only one row expanded at a time per table
const BaseTable = Table;
const AccordionTable: FC<any> = ({ expandable, ...rest }) => {
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  if (!expandable) return <BaseTable {...rest} />;
  return (
    <BaseTable
      {...rest}
      expandable={{
        ...expandable,
        expandedRowKeys: expandedKeys,
        onExpand: (expanded: boolean, record: any) => {
          setExpandedKeys(expanded ? [record.key] : []);
        },
      }}
    />
  );
};

// ============================================================================
// Budget vs Actual (JCR) — static dummy report
// Source: "Budget Vs Actual (JCR) 1.xlsx" → sheet "Budget Vs Actual Vs ETC"
// BOQ activity-wise: Budgeted / Till Date Cost / Balance (ETC) / Revised ETC / Variance
// Clicking a row's Variance Amount expands a Cost-Type breakdown (Material / Man
// Power / Machinery / Other) whose Total Amount (A) equals that row's variance.
// ============================================================================

// Indian-format number; blank/zero rendered as "-"
const fmtIN = (v: number | undefined | null, dec = 0): string => {
  if (v == null || isNaN(Number(v))) return "-";
  return Number(v).toLocaleString("en-IN", {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });
};

// Indian-format with ₹ currency symbol (Rate / Amount columns)
const fmtMoney = (v: number | undefined | null, dec = 0): string => {
  if (v == null || isNaN(Number(v))) return "-";
  return `₹${fmtIN(v, dec)}`;
};

type BvaCell = { qty: number; rate: number; amount: number };
type BvaRow = {
  slNo: number;
  activityCode: string;
  activityDescription: string;
  uom: string;
  budgeted: BvaCell;
  tillDate: BvaCell;
  balance: BvaCell;
  revised: BvaCell;
  variance: BvaCell;
};

const BUDGET_VS_ACTUAL_DATA: BvaRow[] = [
  {
    slNo: 1, activityCode: "DC.1010", uom: "cum",
    activityDescription: "Open Soil excavation inlet and outlet portals",
    budgeted: { qty: 512250, rate: 80, amount: 40980000 },
    tillDate: { qty: 100000, rate: 79.5, amount: 7950000 },
    balance: { qty: 412250, rate: 80, amount: 32980000 },
    revised: { qty: 412250, rate: 79.75, amount: 32876937.5 },
    variance: { qty: 412250, rate: 0.25, amount: 8103062.5 },
  },
  {
    slNo: 2, activityCode: "DC.1020", uom: "cum",
    activityDescription: "Open rock mass  excavation  at inlet and outlet portals",
    budgeted: { qty: 437550, rate: 230, amount: 100636500 },
    tillDate: { qty: 100000, rate: 231, amount: 23100000 },
    balance: { qty: 337550, rate: 230, amount: 77636500 },
    revised: { qty: 337550, rate: 230.5, amount: 77805275 },
    variance: { qty: 337550, rate: -0.5, amount: 22831225 },
  },
  {
    slNo: 3, activityCode: "DC.1030", uom: "cum",
    activityDescription:
      "Underground excavation for tunnel, including temporary provision at the portal for the start of tunnel excavation like canopy structure (false portal) etc. trolley refuge niches, maintenance niches, MVS niches including drilling, blasting, or other means of excavation, including widening of top heading footings, breaking of shotcrete lining in temporary invert, provision of surface drainage, construction ventilation, lighting arrangement during construction, temporary backfilling for traffic in tunnel, profiling / reprofiling of tunnel as per approved cross sections, removal and disposal of excavated material including temporary back fill to dumping site with all lifts and leads up to 1.5 kms from portals as per approved drawings  rates are considered on an average classes by considering Class -II, III, IV.",
    budgeted: { qty: 373478.18, rate: 1000, amount: 373478180 },
    tillDate: { qty: 100000, rate: 999, amount: 99900000 },
    balance: { qty: 273478.18, rate: 1000, amount: 273478180 },
    revised: { qty: 273478.18, rate: 999.5, amount: 273341440.91 },
    variance: { qty: 273478.18, rate: 0.5, amount: 100136739.09 },
  },
  {
    slNo: 4, activityCode: "DC.1040", uom: "Cum",
    activityDescription: "Extra for transportation of spoils over 1.5 km 2% of above items",
    budgeted: { qty: 280108.635, rate: 15, amount: 4201629.525 },
    tillDate: { qty: 100000, rate: 15, amount: 1500000 },
    balance: { qty: 180108.635, rate: 15, amount: 2701629.525 },
    revised: { qty: 180108.635, rate: 15, amount: 2701629.525 },
    variance: { qty: 180108.635, rate: 0, amount: 1500000 },
  },
  {
    slNo: 5, activityCode: "DC.1050", uom: "Nos",
    activityDescription:
      "Pressure relief holes for water pressure 500 no at both portal and 350 no in tunnels considered 15 % where ever necessary considered",
    budgeted: { qty: 850, rate: 550, amount: 467500 },
    tillDate: { qty: 340, rate: 549, amount: 186660 },
    balance: { qty: 510, rate: 550, amount: 280500 },
    revised: { qty: 510, rate: 549.5, amount: 280245 },
    variance: { qty: 510, rate: 0.5, amount: 187255 },
  },
  {
    slNo: 6, activityCode: "DC.1060", uom: "Nos",
    activityDescription:
      "25 mm Dia Placing / , drilling, installation and grouting of SN type rockbolts of the specified length, Fe.500 (slopes, tunnel support & face bolts) as per approved drawings & specifications  The rate shall excluding costs of all materials only  labour, equipments, etc.  considered for the required for the complete job.",
    budgeted: { qty: 10391.04, rate: 708, amount: 7356856.32 },
    tillDate: { qty: 7200, rate: 709, amount: 5104800 },
    balance: { qty: 3191.04, rate: 708, amount: 2259256.32 },
    revised: { qty: 3191.04, rate: 708.5, amount: 2260851.84 },
    variance: { qty: 3191.04, rate: -0.5, amount: 5096004.48 },
  },
  {
    slNo: 7, activityCode: "DC.1070", uom: "Nos",
    activityDescription:
      "32 mm Dia Placing / , drilling, installation and grouting of SN type rockbolts of the specified length, Fe.500 (slopes, tunnel support & face bolts) as per approved drawings & specifications  The rate shall excluding costs of all materials only  labour, equipments, etc.  considered for the required for the complete job.",
    budgeted: { qty: 15586.56, rate: 1085, amount: 16911417.6 },
    tillDate: { qty: 12300, rate: 1084, amount: 13333200 },
    balance: { qty: 3286.56, rate: 1085, amount: 3565917.6 },
    revised: { qty: 3286.56, rate: 1084.5, amount: 3564274.32 },
    variance: { qty: 3286.56, rate: 0.5, amount: 13347143.28 },
  },
  {
    slNo: 8, activityCode: "DC.1080", uom: "MT",
    activityDescription:
      "Fabrication /Placing/ erection & installation of Steel support system and all accessories including all lead, lift, wastage, storing, drilling holes, fixing in phases etc. and installation of accessories for joining the lattice girder segments as per approved workshop drawings of contractor ISHB 200 (40 kg / m) is considered 10 % length of total length @ 1.5 m c/c , (.1*1148*2) = 230 m and total number of steel support shall be 230 nos. peripherical length 35m long each steel rib. Material cost not considered only Erectioning, labour and equipment cost is considered.",
    budgeted: { qty: 200, rate: 81450, amount: 16290000 },
    tillDate: { qty: 100, rate: 81451, amount: 8145100 },
    balance: { qty: 100, rate: 81450, amount: 8145000 },
    revised: { qty: 100, rate: 81450.5, amount: 8145050 },
    variance: { qty: 100, rate: -0.5, amount: 8144950 },
  },
  {
    slNo: 9, activityCode: "DC.1090", uom: "Cum",
    activityDescription:
      "Placement charges for  (M-30)Steel Fibre Reinforcing shotcrete  150 mm , 250 mm , Av. Thickness = 200 mm thick  as per specifications and drawings Excluding all materials. Only labour, equipments, etc. required for complete job  Average area  (7.86+4.86) = 6.36 + 1.64 = 8.0",
    budgeted: { qty: 4591.27912, rate: 7327, amount: 33640302.11 },
    tillDate: { qty: 2219, rate: 7328, amount: 16260832 },
    balance: { qty: 2372.27912, rate: 7327, amount: 17381689.11 },
    revised: { qty: 2372.27912, rate: 7327.5, amount: 17382875.25 },
    variance: { qty: 2372.27912, rate: -0.5, amount: 16257426.86 },
  },
];

// Cost-Type breakdown template (from drilldown image). `weight` carries the
// reference variance split that sums to TEMPLATE_TOTAL_WEIGHT.
type BvaBreakdownItem = { particulars: string; units: string; qty: number; budgetRate: number; weight: number };
type BvaBreakdownGroup = { costType: string; items: BvaBreakdownItem[] };

const VARIANCE_BREAKDOWN_TEMPLATE: BvaBreakdownGroup[] = [
  { costType: "Material", items: [{ particulars: "Nil", units: "", qty: 0, budgetRate: 0, weight: 0 }] },
  {
    costType: "Man Power",
    items: [
      { particulars: "Crew for Shovel", units: "Hour", qty: 8, budgetRate: 373.1, weight: 2985 },
      { particulars: "Crew for Tipper", units: "Hour", qty: 48, budgetRate: 278.9, weight: 13387 },
    ],
  },
  {
    costType: "Machinery",
    items: [
      { particulars: "Shovel 0.85 cum capacity", units: "Hour", qty: 8, budgetRate: 1827.7, weight: 14622 },
      { particulars: "Fuel / Energy charges", units: "Hour", qty: 8, budgetRate: 1447.5, weight: 11580 },
      { particulars: "Tippers 7 cum capacity", units: "Hour", qty: 56, budgetRate: 540.8, weight: 30285 },
      { particulars: "Fuel / Energy charges", units: "Hour", qty: 56, budgetRate: 497.4, weight: 27854 },
    ],
  },
  { costType: "Other", items: [{ particulars: "Nil", units: "", qty: 0, budgetRate: 0, weight: 0 }] },
];

const TEMPLATE_TOTAL_WEIGHT = VARIANCE_BREAKDOWN_TEMPLATE.reduce(
  (sum, g) => sum + g.items.reduce((s, it) => s + it.weight, 0),
  0
);

// Scale the template breakdown so the per-item Variance Amounts sum exactly to
// the supplied row variance (last value-bearing item absorbs the rounding remainder).
const buildVarianceBreakdown = (variance: number) => {
  const target = Math.round(variance);
  let lastG = -1;
  let lastI = -1;
  VARIANCE_BREAKDOWN_TEMPLATE.forEach((g, gi) =>
    g.items.forEach((it, ii) => {
      if (it.weight > 0) {
        lastG = gi;
        lastI = ii;
      }
    })
  );
  let running = 0;
  return VARIANCE_BREAKDOWN_TEMPLATE.map((g, gi) => ({
    costType: g.costType,
    items: g.items.map((it, ii) => {
      let amount = 0;
      if (it.weight > 0) {
        if (gi === lastG && ii === lastI) {
          amount = target - running;
        } else {
          amount = Math.round((it.weight / TEMPLATE_TOTAL_WEIGHT) * target);
          running += amount;
        }
      }
      return { ...it, amount };
    }),
  }));
};

const drillCellStyle: React.CSSProperties = {
  border: "1px solid #e5e5e5",
  padding: "5px 10px",
  fontSize: "12px",
};

// Image-format Cost-Type drilldown for a single activity row's variance.
const VarianceDrilldown: FC<{ variance: number }> = ({ variance }) => {
  const groups = buildVarianceBreakdown(variance);
  // Fixed widths on every column → with tableLayout:fixed + width:100% the
  // leftover panel width is shared proportionally, so the grid fills the whole
  // panel while Particulars (the widest share) stays reasonable.
  const headers: { label: string; width: number; align?: "left" | "right" }[] = [
    { label: "Cost Type", width: 80 },
    { label: "Particulars", width: 260 },
    { label: "Units", width: 70 },
    { label: "Qty", width: 70, align: "right" },
    { label: "Budget Rate", width: 110, align: "right" },
    { label: "current rate", width: 110, align: "right" },
    { label: "Variance Rate", width: 110, align: "right" },
    { label: "Variance Amount", width: 150, align: "right" },
  ];
  return (
    <div style={{ padding: "4px 0", width: "100%" }}>
      <table style={{ borderCollapse: "collapse", width: "100%", tableLayout: "fixed", background: "#fff" }}>
        <colgroup>
          {headers.map((h) => (
            <col key={h.label} style={{ width: h.width }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {headers.map((h) => (
              <th key={h.label} style={{ ...drillCellStyle, background: "#fafafa", fontWeight: 600, textAlign: h.align || "left" }}>
                {h.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <React.Fragment key={g.costType}>
              <tr>
                <td colSpan={8} style={{ ...drillCellStyle, fontWeight: 700 }}>{g.costType} :-</td>
              </tr>
              {g.items.map((it, idx) => (
                <tr key={`${g.costType}-${idx}`}>
                  <td style={drillCellStyle}>{idx + 1}</td>
                  <td style={{ ...drillCellStyle, whiteSpace: "normal", wordBreak: "break-word" }}>{it.particulars}</td>
                  <td style={drillCellStyle}>{it.units}</td>
                  <td style={{ ...drillCellStyle, textAlign: "right" }}>{it.qty || 0}</td>
                  <td style={{ ...drillCellStyle, textAlign: "right" }}>{it.budgetRate ? fmtMoney(it.budgetRate, 2) : 0}</td>
                  <td style={drillCellStyle} />
                  <td style={drillCellStyle} />
                  <td style={{ ...drillCellStyle, textAlign: "right" }}>{it.amount > 0 ? fmtMoney(it.amount) : "-"}</td>
                </tr>
              ))}
              <tr>
                <td colSpan={8} style={{ ...drillCellStyle, padding: "4px", borderLeft: "1px solid #e5e5e5", borderRight: "1px solid #e5e5e5" }} />
              </tr>
            </React.Fragment>
          ))}
          <tr>
            <td style={drillCellStyle} />
            <td style={{ ...drillCellStyle, fontWeight: 700 }}>Total Amount  (A) :</td>
            <td style={drillCellStyle} colSpan={5} />
            <td style={{ ...drillCellStyle, textAlign: "right", fontWeight: 700 }}>{fmtMoney(Math.round(variance))}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const BudgetVsActualReport: FC = () => {
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);

  const toggle = (key: React.Key) =>
    setExpandedKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [key]));

  const num = (v: any) => Number(v || 0);

  // Group of Qty / Rate / Amount columns (replicates Excel colSpan headers)
  const moneyGroup = (title: string, field: keyof BvaRow) => ({
    title,
    children: [
      { title: "Qty", key: `${field}-qty`, width: 110, align: "right" as const, render: (_: any, r: BvaRow) => fmtIN((r[field] as BvaCell).qty, 2) },
      { title: "Rate", key: `${field}-rate`, width: 110, align: "right" as const, render: (_: any, r: BvaRow) => fmtMoney((r[field] as BvaCell).rate, 2) },
      { title: "Amount", key: `${field}-amt`, width: 150, align: "right" as const, render: (_: any, r: BvaRow) => fmtMoney((r[field] as BvaCell).amount) },
    ],
  });

  const columns: any[] = [
    {
      title: "BOQ Details  (Activity Wise)",
      fixed: "left" as const,
      children: [
        { title: "Sl No", dataIndex: "slNo", key: "slNo", width: 60, align: "center" as const, fixed: "left" as const },
        { title: "Activity Code", dataIndex: "activityCode", key: "activityCode", width: 110, fixed: "left" as const },
        {
          title: "Activity Description", dataIndex: "activityDescription", key: "desc", width: 300, ellipsis: true, fixed: "left" as const,
          render: (v: string) => <span title={v}>{v}</span>,
        },
        { title: "UOM", dataIndex: "uom", key: "uom", width: 70, align: "center" as const, fixed: "left" as const },
      ],
    },
    moneyGroup("Budgeted Amount", "budgeted"),
    moneyGroup("Till Date Cost Incurred Amount", "tillDate"),
    moneyGroup("Balance Budget (Estimate to Complete)", "balance"),
    moneyGroup("Revised Estimate to Complete", "revised"),
    {
      title: "Variance (+/-)",
      fixed: "right" as const,
      children: [
        { title: "Qty", key: "var-qty", width: 110, align: "right" as const, fixed: "right" as const, render: (_: any, r: BvaRow) => fmtIN(r.variance.qty, 2) },
        { title: "Rate", key: "var-rate", width: 100, align: "right" as const, fixed: "right" as const, render: (_: any, r: BvaRow) => fmtMoney(r.variance.rate, 2) },
        {
          title: "Variance Amount", key: "var-amt", width: 160, align: "right" as const, fixed: "right" as const,
          render: (_: any, r: BvaRow) => {
            const open = expandedKeys.includes(r.slNo);
            return (
              <a
                onClick={() => toggle(r.slNo)}
                title="View variance breakdown"
                style={{
                  color: "#1890ff",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                  textDecoration: "underline",
                  background: open ? "#e6f4ff" : "transparent",
                  padding: "1px 6px",
                  borderRadius: 4,
                }}
              >
                {fmtMoney(r.variance.amount)}
              </a>
            );
          },
        },
      ],
    },
  ];

  const totals = BUDGET_VS_ACTUAL_DATA.reduce(
    (acc, r) => ({
      budgeted: acc.budgeted + num(r.budgeted.amount),
      tillDate: acc.tillDate + num(r.tillDate.amount),
      balance: acc.balance + num(r.balance.amount),
      revised: acc.revised + num(r.revised.amount),
      variance: acc.variance + num(r.variance.amount),
    }),
    { budgeted: 0, tillDate: 0, balance: 0, revised: 0, variance: 0 }
  );
  const variancePct = totals.budgeted ? (totals.variance / totals.budgeted) * 100 : 0;

  return (
    <Card className="card-bordered p-3 mt-2">
      <h6 className="mb-1">Project Cost Details — Budget Vs Actual &amp; Estimate to Complete</h6>
      <p style={{ color: "#8094ae", fontSize: "12px" }}>
        BOQ activity-wise cost performance till project completion. Click a row&apos;s
        <strong> Variance Amount</strong> to drill down into its Cost-Type breakdown.
      </p>
      <Table
        columns={columns}
        dataSource={BUDGET_VS_ACTUAL_DATA}
        rowKey="slNo"
        size="small"
        pagination={false}
        scroll={{ x: 1900 }}
        expandable={{
          showExpandColumn: false,
          expandedRowKeys: expandedKeys,
          expandedRowRender: (record: BvaRow) => <VarianceDrilldown variance={record.variance.amount} />,
        }}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row style={{ fontWeight: 700, background: "#fafafa" }}>
              <Table.Summary.Cell index={0} colSpan={6}>
                Total BOQ Amount (Direct Cost / Works Cost)
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} align="right">{fmtMoney(totals.budgeted)}</Table.Summary.Cell>
              <Table.Summary.Cell index={7} colSpan={2} />
              <Table.Summary.Cell index={9} align="right">{fmtMoney(totals.tillDate)}</Table.Summary.Cell>
              <Table.Summary.Cell index={10} colSpan={2} />
              <Table.Summary.Cell index={12} align="right">{fmtMoney(totals.balance)}</Table.Summary.Cell>
              <Table.Summary.Cell index={13} colSpan={2} />
              <Table.Summary.Cell index={15} align="right">{fmtMoney(totals.revised)}</Table.Summary.Cell>
              <Table.Summary.Cell index={16} colSpan={2} />
              <Table.Summary.Cell index={18} align="right">
                {fmtMoney(totals.variance)}
                <span style={{ color: "#8094ae", fontWeight: 500 }}> ({variancePct.toFixed(2)}%)</span>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </Card>
  );
};

const BudgetAnalysis: FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { projectName } = (location.state as any) || {};

  // Top-level tab + report sub-tab
  const [activeTab, setActiveTab] = useState<string>("dashboard");
  const [activeReportTab, setActiveReportTab] = useState<string>("report1");

  // Filter states
  const [selectedContractor, setSelectedContractor] = useState<string>("all");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedBudgetLine, setSelectedBudgetLine] = useState<string>("all");
  const [selectedTransactionType, setSelectedTransactionType] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<any>(null);

  // Current project (resolved from getProjectById)
  const [currentProject, setCurrentProject] = useState<{ budgetId?: string; siteId?: string; projectTitle?: string; budgetName?: string } | null>(null);

  // Filter dropdown options (from API)
  const [contractorDropdownOptions, setContractorDropdownOptions] = useState<{ value: string; label: string }[]>([]);
  const [categoryDropdownOptions, setCategoryDropdownOptions] = useState<{ value: string; label: string }[]>([]);
  const [budgetLineDropdownOptions, setBudgetLineDropdownOptions] = useState<{ value: string; label: string }[]>([]);

  // Dashboard API state
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [summaryData, setSummaryData] = useState<any>(null);
  const [categoryData, setCategoryData] = useState<any[]>([]);
  const [consumptionSourceApiData, setConsumptionSourceApiData] = useState<any>(null);
  const [healthData, setHealthData] = useState<any[]>([]);
  const [burnData, setBurnData] = useState<any[]>([]);
  const [distributionByCategoryData, setDistributionByCategoryData] = useState<any[]>([]);
  const [poData, setPoData] = useState<any[]>([]);
  const [woData, setWoData] = useState<any[]>([]);
  const [projectTaskData, setProjectTaskData] = useState<any[]>([]);
  const [expenseData, setExpenseData] = useState<any[]>([]);
  const [contractorData, setContractorData] = useState<any[]>([]);
  const [varianceApiData, setVarianceApiData] = useState<any>(null);
  const [forecastApiData, setForecastApiData] = useState<any>(null);
  const [alertsData, setAlertsData] = useState<any[]>([]);

  // Reports tab API state
  const [reportLoading, setReportLoading] = useState(false);
  const [reportData, setReportData] = useState<any[]>([]);
  const [reportPagination, setReportPagination] = useState({ page: 1, pageSize: 10, total: 0 });

  // Drill-down maps (placeholder — wire to APIs later; structure mirrors source repo)
  const drilldownBudgetLines: Record<string, any[]> = {};
  const drilldownTransactions: Record<string, any[]> = {};
  const drilldownDocuments: Record<string, any[]> = {};
  const drilldownContractorWOs: Record<string, any[]> = {};
  const drilldownWOBills: Record<string, any[]> = {};
  const drilldownBudgetLinePRs: Record<string, any[]> = {};
  const drilldownPRDocuments: Record<string, any[]> = {};
  const drilldownExpenseEntries: Record<string, any[]> = {};
  const drilldownExpenseDocuments: Record<string, any[]> = {};
  const drilldownWorkPackageWOs: Record<string, any[]> = {};
  const drilldownBillDocuments: Record<string, any[]> = {};
  const drilldownMonthTransactions: Record<string, any[]> = {};
  const drilldownMonthTxnDocuments: Record<string, any[]> = {};

  const transactionTypeOptions = [
    { value: "po", label: "Purchase Order" },
    { value: "wo", label: "Work Order" },
    { value: "expense", label: "Expense" },
    { value: "invoice", label: "Invoice" },
  ];

  // Load menu data on component mount to ensure navbar is visible
  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => {
          return {
            ...item,
            linkState: location.state,
          };
        })
      )
    );
  }, [dispatch, location]);

  // Fetch project (budgetId, siteId) + filter dropdowns
  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const projectRes: any = await projectsApis.getProjectById(projectId, undefined);
        const projectData = projectRes?.data || {};
        const budgetId = projectData.budgetId ? String(projectData.budgetId) : undefined;
        const siteId = projectData.siteId ? String(projectData.siteId) : undefined;
        setCurrentProject({
          budgetId,
          siteId,
          projectTitle: projectData.projectTitle,
          budgetName: projectData.budgetName,
        });

        const pickList = (r: any) => {
          if (r.status !== "fulfilled") return [];
          const v = r.value;
          return v?.response?.data || v?.data || v?.response || [];
        };

        const params: any = {};
        if (budgetId) params.headerIds = budgetId;
        if (siteId) params.siteIds = siteId;

        const [contractorRes, categoryRes, lineRes] = await Promise.allSettled([
          siteId
            ? contractorsApis.fetchContractorBySiteId({ id: siteId })
            : Promise.resolve({ data: [] } as any),
          budgetApis.budgetDashboardCategoryDropdown(params),
          budgetApis.budgetLinesDropdown(budgetId ? { headerId: budgetId } : {}),
        ]);

        // Contractors (PMS API: data[] with { companyName, contractorID })
        const contractors: any[] = pickList(contractorRes);
        setContractorDropdownOptions(
          contractors.map((c: any) => ({
            value: String(c.contractorID ?? c.contractorId ?? c.id ?? c.value),
            label: String(c.companyName ?? c.contractorName ?? c.name ?? c.label ?? ""),
          }))
        );

        // Categories
        const categories: any[] = pickList(categoryRes);
        setCategoryDropdownOptions(
          categories.map((c: any) => ({
            value: String(c.budgetCategoryId ?? c.categoryId ?? c.id ?? c.value),
            label: String(c.categoryName ?? c.budgetCategoryName ?? c.name ?? c.label ?? ""),
          }))
        );

        // Budget Lines
        const lines: any[] = pickList(lineRes);
        setBudgetLineDropdownOptions(
          lines.map((l: any) => ({
            value: String(l.lineId ?? l.budgetLineId ?? l.id ?? l.value),
            label: String(l.lineName ?? l.budgetLineName ?? l.name ?? l.label ?? ""),
          }))
        );
      } catch (err) {
        console.error("Error loading project / filter dropdowns", err);
      }
    })();
  }, [projectId]);

  // Build common filter params applied to dashboard calls
  const buildFilterParams = () => {
    const params: any = {};
    if (currentProject?.budgetId) params.headerIds = currentProject.budgetId;
    if (currentProject?.siteId) params.siteIds = currentProject.siteId;
    if (selectedContractor && selectedContractor !== "all") params.contractorIds = selectedContractor;
    if (selectedCategory && selectedCategory !== "all") params.budgetCategoryIds = selectedCategory;
    if (selectedBudgetLine && selectedBudgetLine !== "all") params.lineIds = selectedBudgetLine;
    if (dateRange && dateRange[0] && dateRange[1]) {
      params.startDate = dateRange[0].format ? dateRange[0].format("YYYY-MM-DD") : dateRange[0];
      params.endDate = dateRange[1].format ? dateRange[1].format("YYYY-MM-DD") : dateRange[1];
    }
    return params;
  };

  // Fetch dashboard data from API
  const fetchDashboardData = async () => {
    setDashboardLoading(true);
    try {
      const params: any = { ...buildFilterParams() };
      if (selectedTransactionType.length > 0) {
        const moduleMap: Record<string, string> = {
          po: "Procurement",
          invoice: "Procurement",
          wo: "Work Order",
          expense: "Expense",
        };
        const modules = Array.from(new Set(selectedTransactionType.map((t) => moduleMap[t]).filter(Boolean)));
        if (modules.length > 0) params.modules = modules.join(",");
      }

      const results = await Promise.allSettled([
        budgetAnalysisDashboardApis.summaryReport(params),
        budgetAnalysisDashboardApis.budgetVsActualByCategory(params),
        budgetAnalysisDashboardApis.budgetConsumptionBySource(params),
        budgetAnalysisDashboardApis.budgetHealthIndicator(params),
        budgetAnalysisDashboardApis.timeBudgetBurn(params),
        budgetAnalysisDashboardApis.budgetDistributionByCategory(params),
        budgetAnalysisDashboardApis.budgetVsPurchaseOrders(params),
        budgetAnalysisDashboardApis.budgetVsWorkOrders(params),
        budgetAnalysisDashboardApis.budgetVsProjectTasks(params),
        budgetAnalysisDashboardApis.expenseBudgetAnalysis(params),
        budgetAnalysisDashboardApis.contractorCostPerformance(params),
        budgetAnalysisDashboardApis.budgetVarianceAnalysis(params),
        budgetAnalysisDashboardApis.forecastCostAtCompletion(params),
        budgetAnalysisDashboardApis.budgetControlAlerts(params),
      ]);

      const getData = (result: any) => {
        if (result.status === "fulfilled") {
          const res = result.value;
          return res?.response?.data || res?.data || [];
        }
        return [];
      };

      const addKeys = (arr: any[]) =>
        Array.isArray(arr) ? arr.map((item: any, idx: number) => ({ ...item, key: idx })) : [];

      setSummaryData(getData(results[0])?.[0] || null);
      setCategoryData(addKeys(getData(results[1])));
      setConsumptionSourceApiData(getData(results[2])?.[0] || null);
      setHealthData(addKeys(getData(results[3])));
      setBurnData(addKeys(getData(results[4])));
      setDistributionByCategoryData(addKeys(getData(results[5])));
      setPoData(addKeys(getData(results[6])));
      setWoData(addKeys(getData(results[7])));
      setProjectTaskData(addKeys(getData(results[8])));
      setExpenseData(addKeys(getData(results[9])));
      setContractorData(addKeys(getData(results[10])));
      setVarianceApiData(getData(results[11])?.[0] || null);
      setForecastApiData(getData(results[12])?.[0] || null);
      setAlertsData(addKeys(getData(results[13])));
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setDashboardLoading(false);
    }
  };

  useEffect(() => {
    if (!currentProject?.budgetId) return;
    if (activeTab !== "dashboard") return;
    fetchDashboardData();
  }, [activeTab, currentProject?.budgetId, currentProject?.siteId, selectedTransactionType, selectedContractor, selectedCategory, selectedBudgetLine, dateRange]);

  // Fetch report data from API
  const fetchReportData = async (reportTab: string, page = 1, pageSize = 10) => {
    // Report 1 (Budget vs Actual) is a static BOQ report — no API call.
    if (reportTab === "report1") {
      setReportLoading(false);
      return;
    }
    setReportLoading(true);
    try {
      const params: any = { page, pageSize, ...buildFilterParams() };
      if (selectedTransactionType.length > 0) {
        const moduleMap: Record<string, string> = {
          po: "Procurement",
          invoice: "Procurement",
          wo: "Work Order",
          expense: "Expense",
        };
        const modules = Array.from(new Set(selectedTransactionType.map((t) => moduleMap[t]).filter(Boolean)));
        if (modules.length > 0) params.modules = modules.join(",");
      }

      let apiCall;
      switch (reportTab) {
        case "report1": apiCall = budgetAnalysisDashboardApis.projectBudgetVsActualSummary; break;
        case "report2": apiCall = budgetAnalysisDashboardApis.budgetConsumptionByCategorySummary; break;
        case "report3": apiCall = budgetAnalysisDashboardApis.contractorCostSummary; break;
        case "report4": apiCall = budgetAnalysisDashboardApis.budgetVsPurchaseOrdersSummary; break;
        case "report5": apiCall = budgetAnalysisDashboardApis.budgetVsExpensesSummary; break;
        case "report6": apiCall = budgetAnalysisDashboardApis.budgetVsWorkOrdersSummary; break;
        case "report7": apiCall = budgetAnalysisDashboardApis.budgetUtilizationTrend; break;
        case "report8": apiCall = budgetAnalysisDashboardApis.budgetForecastSummary; break;
        default: return;
      }

      const res: any = await apiCall(params);
      const data = res?.response?.data || res?.data || [];
      const total = Number(res?.response?.totalCount || data.length);
      setReportData(data.map((item: any, idx: number) => ({ ...item, key: idx })));
      setReportPagination({ page, pageSize, total });
    } catch (error) {
      console.error("Error fetching report data:", error);
      setReportData([]);
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== "reports") return;
    if (!currentProject?.budgetId) return;
    fetchReportData(activeReportTab);
  }, [activeTab, activeReportTab, currentProject?.budgetId, currentProject?.siteId, selectedTransactionType, selectedContractor, selectedCategory, selectedBudgetLine, dateRange]);

  // Chart refs
  const consumptionSourceChartRef = useRef<HTMLDivElement>(null);
  const budgetBurnChartRef = useRef<HTMLDivElement>(null);
  const budgetDistributionChartRef = useRef<HTMLDivElement>(null);
  const utilizationTrendChartRef = useRef<HTMLDivElement>(null);

  const consumptionSourceChart = useRef<echarts.ECharts | null>(null);
  const budgetBurnChart = useRef<echarts.ECharts | null>(null);
  const budgetDistributionChart = useRef<echarts.ECharts | null>(null);
  const utilizationTrendChart = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (activeTab !== "dashboard") return;
    setTimeout(() => {
      initBudgetDistributionChart();
      initConsumptionSourceChart();
      initBudgetBurnChart();
    }, 100);
    return () => {
      consumptionSourceChart.current?.dispose();
      budgetBurnChart.current?.dispose();
      budgetDistributionChart.current?.dispose();
      consumptionSourceChart.current = null;
      budgetBurnChart.current = null;
      budgetDistributionChart.current = null;
    };
  }, [activeTab, categoryData, consumptionSourceApiData, burnData]);

  useEffect(() => {
    if (activeTab === "reports" && activeReportTab === "report7") {
      setTimeout(() => {
        initUtilizationTrendChart();
      }, 150);
    }
    return () => {
      utilizationTrendChart.current?.dispose();
      utilizationTrendChart.current = null;
    };
  }, [activeTab, activeReportTab, reportData]);

  useEffect(() => {
    const handleResize = () => {
      consumptionSourceChart.current?.resize();
      budgetBurnChart.current?.resize();
      budgetDistributionChart.current?.resize();
      utilizationTrendChart.current?.resize();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const initBudgetDistributionChart = () => {
    if (!budgetDistributionChartRef.current) return;
    if (budgetDistributionChart.current) budgetDistributionChart.current.dispose();
    budgetDistributionChart.current = echarts.init(budgetDistributionChartRef.current);
    const categories = categoryData.map((d: any) => d.budgetCategory || d.category || "");
    const chartData = categoryData.map((d: any) => ({
      budget: Number(d.totalBudget ?? d.budget ?? 0),
      committed: Number(d.totalCommitted ?? d.committed ?? 0),
      consumed: Number(d.totalConsumed ?? d.consumed ?? 0),
    }));
    budgetDistributionChart.current.setOption({
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params: any) => {
          let html = `<strong>${params[0].name}</strong>`;
          params.forEach((p: any) => {
            html += `<br/>${p.marker} ${p.seriesName}: ${formatCr(p.value)}`;
          });
          return html;
        },
      },
      legend: { data: ["Budget", "Committed", "Consumed"], bottom: 0 },
      grid: { left: "3%", right: "4%", bottom: "15%", top: "8%", containLabel: true },
      xAxis: { type: "category", data: categories, axisLabel: { rotate: 15 } },
      yAxis: {
        type: "value",
        axisLabel: { formatter: (v: number) => `${(v / 10000000).toFixed(0)}Cr` },
      },
      series: [
        {
          name: "Budget",
          type: "bar",
          data: chartData.map((d: any) => d.budget),
          itemStyle: { color: "#5470C6" },
          barWidth: "22%",
        },
        {
          name: "Committed",
          type: "bar",
          data: chartData.map((d: any) => d.committed),
          itemStyle: { color: "#91CC75" },
          barWidth: "22%",
        },
        {
          name: "Consumed",
          type: "bar",
          data: chartData.map((d: any) => d.consumed),
          itemStyle: { color: "#FAC858" },
          barWidth: "22%",
        },
      ],
    });
  };

  const initConsumptionSourceChart = () => {
    if (!consumptionSourceChartRef.current) return;
    if (consumptionSourceChart.current) consumptionSourceChart.current.dispose();
    consumptionSourceChart.current = echarts.init(consumptionSourceChartRef.current);
    const pieData: { name: string; value: number; pct: number }[] = (() => {
      if (consumptionSourceApiData) {
        const d = consumptionSourceApiData;
        return [
          { name: "Procurement (PO + GRN)", value: Number(d.procurementConsumed ?? d.poInvoices ?? 0), pct: Number(d.procurementPct ?? 0) },
          { name: "Work Orders (Contractors)", value: Number(d.workOrderConsumed ?? d.woRaBills ?? 0), pct: Number(d.workOrderPct ?? 0) },
          { name: "Project Task Expenses", value: Number(d.projectTaskConsumed ?? 0), pct: Number(d.projectTaskPct ?? 0) },
          { name: "Petty Cash Expenses", value: Number(d.expenseConsumed ?? d.expenses ?? 0), pct: Number(d.expensePct ?? 0) },
        ];
      }
      return [];
    })();

    consumptionSourceChart.current.setOption({
      tooltip: {
        trigger: "item",
        confine: true,
        formatter: (params: any) => {
          const apiPct = params.data?.pct;
          const pctText = apiPct != null && !isNaN(apiPct) ? `${apiPct}%` : `${params.percent}%`;
          return `${params.name}: ${formatCr(params.value)} (${pctText})`;
        },
      },
      legend: { orient: "horizontal", bottom: 0, left: "center", textStyle: { fontSize: 11 } },
      grid: { containLabel: true },
      series: [
        {
          name: "Consumption Source",
          type: "pie",
          radius: ["35%", "60%"],
          center: ["50%", "45%"],
          avoidLabelOverlap: true,
          itemStyle: { borderRadius: 6, borderColor: "#fff", borderWidth: 2 },
          label: { show: false },
          emphasis: { label: { show: false } },
          data: pieData,
          color: ["#5470C6", "#91CC75", "#FAC858", "#EE6666"],
        },
      ],
    });
  };

  const initUtilizationTrendChart = () => {
    if (!utilizationTrendChartRef.current) return;
    if (utilizationTrendChart.current) utilizationTrendChart.current.dispose();
    utilizationTrendChart.current = echarts.init(utilizationTrendChartRef.current);
    const source = reportData;
    const months = source.map((d: any) => {
      const name = d.monthName || d.fullMonthName || d.month || "";
      return typeof name === "string" ? name.substring(0, 3) : String(name);
    });
    const values = source.map((d: any) => Number(d.consumedValue ?? d.budgetConsumed ?? 0));
    utilizationTrendChart.current.setOption({
      tooltip: {
        trigger: "axis",
        formatter: (params: any) =>
          `<strong>${params[0].name}</strong><br/>Budget Consumed: ${formatCr(params[0].value)}`,
      },
      grid: { left: "3%", right: "4%", bottom: "10%", top: "8%", containLabel: true },
      xAxis: { type: "category", data: months },
      yAxis: {
        type: "value",
        axisLabel: { formatter: (v: number) => `${(v / 10000000).toFixed(0)}Cr` },
      },
      series: [
        {
          name: "Budget Consumed",
          type: "line",
          data: values,
          lineStyle: { color: "#5470C6", width: 3 },
          itemStyle: { color: "#5470C6" },
          areaStyle: { color: "rgba(84, 112, 198, 0.15)" },
          smooth: true,
        },
      ],
    });
  };

  const initBudgetBurnChart = () => {
    if (!budgetBurnChartRef.current) return;
    if (budgetBurnChart.current) budgetBurnChart.current.dispose();
    budgetBurnChart.current = echarts.init(budgetBurnChartRef.current);
    const activeMonths = burnData.map((d: any) => d.monthLabel || d.monthName || d.month || "");
    const activeBurn = burnData.map((d: any) => Number(d.totalMonthlyBurn ?? d.burn ?? d.monthlyBurn ?? 0));
    const activeCum = burnData.map((d: any) => Number(d.cumulativeBurn ?? d.cumulative ?? 0));
    budgetBurnChart.current.setOption({
      tooltip: {
        trigger: "axis",
        formatter: (params: any) => {
          let html = `<strong>${params[0].name}</strong>`;
          params.forEach((p: any) => {
            html += `<br/>${p.marker} ${p.seriesName}: ${formatCr(p.value)}`;
          });
          return html;
        },
      },
      legend: { data: ["Monthly Burn", "Cumulative"], bottom: 0 },
      grid: { left: "3%", right: "4%", bottom: "15%", top: "8%", containLabel: true },
      xAxis: { type: "category", data: activeMonths },
      yAxis: {
        type: "value",
        axisLabel: { formatter: (v: number) => `${(v / 10000000).toFixed(0)}Cr` },
      },
      series: [
        {
          name: "Monthly Burn",
          type: "bar",
          data: activeBurn,
          itemStyle: { color: "#5470C6" },
          barWidth: "40%",
        },
        {
          name: "Cumulative",
          type: "line",
          data: activeCum,
          lineStyle: { color: "#EE6666", width: 3 },
          itemStyle: { color: "#EE6666" },
          smooth: true,
        },
      ],
    });
  };

  const renderFilters = () => (
    <Card className="card-bordered mb-3">
      <div style={{ padding: "16px 20px" }}>
        <div className="d-flex align-items-center mb-2">
          <FilterOutlined style={{ marginRight: 8, color: "#1890ff" }} />
          <strong style={{ fontSize: "14px" }}>Filters</strong>
        </div>
        <Row className="g-2">
          <Col lg="2" md="4" sm="6">
            <label className="form-label" style={{ fontSize: "12px", color: "#8094ae" }}>Project</label>
            <Select
              value={currentProject?.budgetId || ""}
              style={{ width: "100%" }}
              disabled
            >
              {currentProject?.budgetId && (
                <Option value={currentProject.budgetId}>
                  {projectName || currentProject.projectTitle || currentProject.budgetName || "Current Project"}
                </Option>
              )}
            </Select>
          </Col>
          <Col lg="2" md="4" sm="6">
            <label className="form-label" style={{ fontSize: "12px", color: "#8094ae" }}>Contractor</label>
            <Select
              value={selectedContractor}
              onChange={setSelectedContractor}
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="children"
            >
              <Option value="all">All Contractors</Option>
              {contractorDropdownOptions.map((o) => (
                <Option key={o.value} value={o.value}>{o.label}</Option>
              ))}
            </Select>
          </Col>
          <Col lg="2" md="4" sm="6">
            <label className="form-label" style={{ fontSize: "12px", color: "#8094ae" }}>Budget Category</label>
            <Select
              value={selectedCategory}
              onChange={setSelectedCategory}
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="children"
            >
              <Option value="all">All Categories</Option>
              {categoryDropdownOptions.map((o) => (
                <Option key={o.value} value={o.value}>{o.label}</Option>
              ))}
            </Select>
          </Col>
          <Col lg="2" md="4" sm="6">
            <label className="form-label" style={{ fontSize: "12px", color: "#8094ae" }}>Date Range</label>
            <RangePicker
              style={{ width: "100%" }}
              onChange={(dates) => setDateRange(dates)}
            />
          </Col>
          <Col lg="2" md="4" sm="6">
            <label className="form-label" style={{ fontSize: "12px", color: "#8094ae" }}>Budget Line</label>
            <Select
              value={selectedBudgetLine}
              onChange={setSelectedBudgetLine}
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="children"
            >
              <Option value="all">All Budget Lines</Option>
              {budgetLineDropdownOptions.map((o) => (
                <Option key={o.value} value={o.value}>{o.label}</Option>
              ))}
            </Select>
          </Col>
          <Col lg="2" md="4" sm="6">
            <label className="form-label" style={{ fontSize: "12px", color: "#8094ae" }}>Txn Type</label>
            <Select
              value={selectedTransactionType}
              onChange={setSelectedTransactionType}
              style={{ width: "100%" }}
              mode="multiple"
              maxTagCount={1}
              placeholder="All Transactions"
              allowClear
            >
              {transactionTypeOptions.map((o) => (
                <Option key={o.value} value={o.value}>{o.label}</Option>
              ))}
            </Select>
          </Col>
        </Row>
      </div>
    </Card>
  );

  const getHealthIcon = (status: string) => {
    if (status === "healthy") return <span style={{ fontSize: "18px" }}>🟢</span>;
    if (status === "monitor") return <span style={{ fontSize: "18px" }}>🟠</span>;
    return <span style={{ fontSize: "18px" }}>🔴</span>;
  };

  const n = (val: any) => Number(val || 0);

  const kpiCards = summaryData ? [
    { title: "Total Budget", value: Number(summaryData.totalBudget || 0), icon: <DollarOutlined />, color: "#1890ff" },
    { title: "Total Committed", value: Number(summaryData.totalCommitted || 0), icon: <BarChartOutlined />, color: "#722ed1", description: "PO + Work Orders" },
    { title: "Total Consumed", value: Number(summaryData.totalConsumed || 0), icon: <PieChartOutlined />, color: "#faad14", description: "Invoices + Expenses + RA Bills" },
    { title: "Available Budget", value: Number(summaryData.availableBudget || 0), icon: <CheckCircleOutlined />, color: "#52c41a" },
    { title: "Forecast at Completion", value: Number(summaryData.forecastAtCompletion || 0), icon: <FundProjectionScreenOutlined />, color: "#13c2c2" },
    { title: "Budget Variance", value: Number(summaryData.budgetVariance || 0), icon: <ArrowUpOutlined />, color: "#52c41a", trend: Number(summaryData.budgetVariance || 0) >= 0 ? "saving" : "overrun" },
  ] : [];

  const reportTablePagination = {
    current: reportPagination.page,
    pageSize: reportPagination.pageSize,
    total: reportPagination.total,
    onChange: (p: number, ps: number) => fetchReportData(activeReportTab, p, ps),
  };

  const renderDashboard = () => (
    <Spin spinning={dashboardLoading}>
      <div>
            {/* Row 1: 6 KPI Cards */}
            <Row className="g-gs mb-3">
              {kpiCards.map((kpi: any, idx: number) => (
                <Col lg="2" md="4" sm="6" key={idx}>
                  <Card
                    className="card-bordered"
                    style={{ borderLeft: `4px solid ${kpi.color}`, padding: "14px 16px", height: "120px", display: "flex", flexDirection: "column", justifyContent: "center" }}
                  >
                    <div>
                      <div className="d-flex justify-content-between align-items-start">
                        <p className="mb-1" style={{ color: "#8094ae", fontSize: "12px", fontWeight: 500 }}>
                          {kpi.title}
                        </p>
                        <span style={{ fontSize: "22px", color: kpi.color, opacity: 0.7 }}>
                          {kpi.icon}
                        </span>
                      </div>
                      <h5 className="mb-0" style={{ fontSize: "16px" }}>
                        {formatCr(kpi.value)}
                      </h5>
                      {kpi.description && (
                        <small style={{ color: "#8094ae", fontSize: "10px" }}>
                          {kpi.description}
                        </small>
                      )}
                      {kpi.trend === "saving" && (
                        <small style={{ color: "#52c41a", fontSize: "11px" }}>
                          <ArrowUpOutlined /> Saving
                        </small>
                      )}
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Row 2: Budget vs Actual by Category (Bar Chart) + Budget Consumption by Source (Pie Chart) */}
            <Row className="g-gs mb-3">
              <Col lg="8">
                <Card className="card-bordered p-3 h-100">
                  <h6 className="mb-2">Budget vs Actual vs Committed by Category</h6>
                  <div ref={budgetDistributionChartRef} style={{ width: "100%", height: "350px" }} />
                </Card>
              </Col>
              <Col lg="4">
                <Card className="card-bordered p-3 h-100">
                  <h6 className="mb-2">Budget Consumption by Source</h6>
                  <div ref={consumptionSourceChartRef} style={{ width: "100%", height: "350px" }} />
                </Card>
              </Col>
            </Row>

            {/* Row 3: Budget Health Indicator + Time vs Budget Burn */}
            <Row className="g-gs mb-3">
              <Col lg="5">
                <Card className="card-bordered p-3 h-100">
                  <h6 className="mb-2">Budget Health Indicator</h6>
                  <p style={{ color: "#8094ae", fontSize: "11px", marginBottom: "8px" }}>
                    🟢 &lt;70% Healthy &nbsp; 🟠 70-90% Monitor &nbsp; 🔴 &gt;90% Risk
                  </p>
                  <div style={{ maxHeight: 300, overflowY: "auto" }}>
                    <AccordionTable
                      columns={[
                        {
                          title: "Category",
                          key: "category",
                          width: 150,
                          render: (_: any, r: any) => r.budgetCategory || r.category || "",
                        },
                        {
                          title: "Utilization %",
                          key: "utilization",
                          width: 180,
                          render: (_: any, r: any) => {
                            const pct = Math.round(n(r.totalUtilizationPct ?? r.utilization));
                            return (
                              <Progress
                                percent={pct}
                                size="small"
                                strokeColor={pct >= 90 ? "#ff4d4f" : pct >= 70 ? "#faad14" : "#52c41a"}
                              />
                            );
                          },
                        },
                        {
                          title: "Status",
                          key: "status",
                          width: 80,
                          render: (_: string, record: any) =>
                            getHealthIcon(String(record.trafficLight ?? record.status ?? "").toLowerCase()),
                        },
                      ]}
                      dataSource={healthData}
                      pagination={false}
                      size="small"
                      scroll={{ x: "max-content" }}
                    />
                  </div>
                </Card>
              </Col>
              <Col lg="7">
                <Card className="card-bordered p-3 h-100">
                  <h6 className="mb-2">Time vs Budget Burn</h6>
                  <div ref={budgetBurnChartRef} style={{ width: "100%", height: "320px" }} />
                </Card>
              </Col>
            </Row>

            {/* Row 4: Budget Distribution by Category Table */}
            <Row className="g-gs mb-3">
              <Col lg="12">
                <Card className="card-bordered p-3">
                  <h6 className="mb-2">Budget Distribution by Category</h6>
                  <div style={{ maxHeight: 300, overflowY: "auto" }}>
                    <AccordionTable
                      columns={[
                        {
                          title: "Category",
                          key: "category",
                          width: 150,
                          render: (_: any, r: any) => r.budgetCategory || r.category || "",
                        },
                        {
                          title: "Budget",
                          key: "budget",
                          width: 130,
                          render: (_: any, r: any) => formatCr(n(r.budgetValue ?? r.budget)),
                        },
                        {
                          title: "Committed",
                          key: "committed",
                          width: 130,
                          render: (_: any, r: any) => formatCr(n(r.committedValue ?? r.committed)),
                        },
                        {
                          title: "Consumed",
                          key: "consumed",
                          width: 130,
                          render: (_: any, r: any) => formatCr(n(r.consumedValue ?? r.consumed)),
                        },
                        {
                          title: "Available",
                          key: "available",
                          width: 130,
                          render: (_: any, r: any) => {
                            const val = n(r.availableBudget ?? r.available);
                            return <span style={{ color: val >= 0 ? "#52c41a" : "#ff4d4f" }}>{formatCr(val)}</span>;
                          },
                        },
                        {
                          title: "Utilization %",
                          key: "utilization",
                          width: 150,
                          render: (_: any, r: any) => {
                            const apiPct = r.totalUtilizationPct != null ? Number(r.totalUtilizationPct) : null;
                            let pct: number;
                            if (apiPct != null && !isNaN(apiPct)) {
                              pct = Math.round(apiPct);
                            } else {
                              const budget = n(r.budgetValue ?? r.budget);
                              pct = budget > 0 ? Math.round(((n(r.committedValue ?? r.committed) + n(r.consumedValue ?? r.consumed)) / budget) * 100) : 0;
                            }
                            return <Progress percent={pct} size="small" strokeColor={pct >= 90 ? "#ff4d4f" : pct >= 70 ? "#faad14" : "#52c41a"} />;
                          },
                        },
                      ]}
                      dataSource={distributionByCategoryData}
                      pagination={false}
                      size="small"
                      scroll={{ x: 820 }}
                    />
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Row 5: Budget vs Purchase Orders */}
            <Row className="g-gs mb-3">
              <Col lg="12">
                <Card className="card-bordered p-3">
                  <h6 className="mb-2">Budget vs Purchase Orders</h6>
                  <div style={{ maxHeight: 300, overflowY: "auto" }}>
                    <AccordionTable
                      columns={[
                        {
                          title: "Budget Line",
                          key: "budgetLine",
                          width: 180,
                          ellipsis: true,
                          render: (_: any, r: any) => r.budgetLineName || r.budgetLine || "",
                        },
                        {
                          title: "Budget",
                          key: "budget",
                          width: 130,
                          render: (_: any, r: any) => formatCr(n(r.budgetValue ?? r.budget)),
                        },
                        {
                          title: "PO Value",
                          key: "poValue",
                          width: 120,
                          render: (_: any, r: any) => formatCr(n(r.poValue)),
                        },
                        {
                          title: "GRN Value",
                          key: "grnValue",
                          width: 120,
                          render: (_: any, r: any) => formatCr(n(r.grnValue)),
                        },
                        {
                          title: "Invoice Value",
                          key: "invoiceValue",
                          width: 130,
                          render: (_: any, r: any) => formatCr(n(r.invoiceValue)),
                        },
                        {
                          title: "PO vs Budget %",
                          key: "poPct",
                          width: 160,
                          render: (_: any, r: any) => {
                            const apiPct = r.poVsBudgetPct != null ? Number(r.poVsBudgetPct) : null;
                            let pct: number;
                            if (apiPct != null && !isNaN(apiPct)) {
                              pct = Math.round(apiPct);
                            } else {
                              const budget = n(r.budgetValue ?? r.budget);
                              pct = budget > 0 ? Math.round((n(r.poValue) / budget) * 100) : 0;
                            }
                            return (
                              <Progress
                                percent={Math.min(pct, 100)}
                                format={() => `${pct}%`}
                                size="small"
                                strokeColor={pct >= 90 ? "#ff4d4f" : pct >= 70 ? "#faad14" : "#52c41a"}
                              />
                            );
                          },
                        },
                        {
                          title: "Status",
                          key: "trafficLight",
                          width: 120,
                          render: (_: any, r: any) => {
                            const s = r.trafficLight || "";
                            const color =
                              s === "Healthy" ? "green" :
                              s === "Monitor" ? "orange" :
                              s === "Risk" ? "red" :
                              s === "No Activity" ? "default" :
                              s ? "blue" : "default";
                            return s ? <Tag color={color}>{s}</Tag> : null;
                          },
                        },
                      ]}
                      dataSource={poData}
                      pagination={false}
                      size="small"
                      scroll={{ x: 960 }}
                    />
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Row 6: Budget vs Work Orders */}
            <Row className="g-gs mb-3">
              <Col lg="12">
                <Card className="card-bordered p-3">
                  <h6 className="mb-2">Budget vs Work Orders</h6>
                  <div style={{ maxHeight: 300, overflowY: "auto" }}>
                    <AccordionTable
                      columns={[
                        { title: "Work Package", dataIndex: "workPackage", key: "workPackage", width: 160 },
                        { title: "Contractor", dataIndex: "contractor", key: "contractor", width: 160 },
                        { title: "Budget", dataIndex: "budget", key: "budget", width: 120, render: (v: any) => formatCr(n(v)) },
                        { title: "WO Value", dataIndex: "woValue", key: "woValue", width: 120, render: (v: any) => formatCr(n(v)) },
                        { title: "RA Bills", dataIndex: "raBills", key: "raBills", width: 120, render: (v: any) => formatCr(n(v)) },
                        { title: "Balance", dataIndex: "balance", key: "balance", width: 120, render: (v: any) => <span style={{ color: "#52c41a" }}>{formatCr(n(v))}</span> },
                      ]}
                      dataSource={woData}
                      pagination={false}
                      size="small"
                      scroll={{ x: 800 }}
                    />
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Row 7: Budget vs Project Tasks + Expense Budget Analysis */}
            <Row className="g-gs mb-3">
              <Col lg="6">
                <Card className="card-bordered p-3 h-100">
                  <h6 className="mb-2">Budget vs Project Tasks</h6>
                  <div style={{ maxHeight: 300, overflowY: "auto" }}>
                    <AccordionTable
                      columns={[
                        {
                          title: "Task",
                          key: "task",
                          width: 200,
                          ellipsis: true,
                          render: (_: any, r: any) => r.taskName || r.task || "",
                        },
                        {
                          title: "Budget",
                          key: "budget",
                          width: 120,
                          render: (_: any, r: any) => formatCr(n(r.taskBudgetAllocated ?? r.budget)),
                        },
                        {
                          title: "Spent",
                          key: "spent",
                          width: 120,
                          render: (_: any, r: any) => formatCr(n(r.taskSpent ?? r.spent)),
                        },
                        {
                          title: "Balance",
                          key: "balance",
                          width: 120,
                          render: (_: any, r: any) => {
                            const val = n(r.taskBalance ?? r.balance);
                            return <span style={{ color: val >= 0 ? "#52c41a" : "#ff4d4f" }}>{formatCr(val)}</span>;
                          },
                        },
                        {
                          title: "Status",
                          key: "taskStatus",
                          width: 110,
                          render: (_: any, r: any) => {
                            const s = r.taskStatus || "";
                            const color = s === "On Track" ? "green" : s === "Near Budget" ? "orange" : s ? "red" : "default";
                            return s ? <Tag color={color}>{s}</Tag> : null;
                          },
                        },
                      ]}
                      dataSource={projectTaskData}
                      pagination={false}
                      size="small"
                      scroll={{ x: 510 }}
                    />
                  </div>
                </Card>
              </Col>

              <Col lg="6">
                <Card className="card-bordered p-3 h-100">
                  <h6 className="mb-2">Expense Budget Analysis</h6>
                  <div style={{ maxHeight: 300, overflowY: "auto" }}>
                    <AccordionTable
                      columns={[
                        {
                          title: "Expense",
                          key: "expenseName",
                          width: 180,
                          ellipsis: true,
                          render: (_: any, r: any) => r.expenseName || r.expenceNumber || "",
                        },
                        {
                          title: "Budget Line",
                          key: "budgetLineName",
                          width: 160,
                          ellipsis: true,
                          render: (_: any, r: any) => r.budgetLineName || "",
                        },
                        {
                          title: "Budget",
                          key: "budget",
                          width: 110,
                          render: (_: any, r: any) => formatCr(n(r.expenseLineBudget ?? r.budget)),
                        },
                        {
                          title: "Consumed",
                          key: "consumed",
                          width: 110,
                          render: (_: any, r: any) => formatCr(n(r.expenseConsumed ?? r.actual)),
                        },
                        {
                          title: "Available",
                          key: "available",
                          width: 120,
                          render: (_: any, r: any) => {
                            const val = n(r.expenseAvailable);
                            return <span style={{ color: val >= 0 ? "#52c41a" : "#ff4d4f" }}>{formatCr(val)}</span>;
                          },
                        },
                        {
                          title: "Util %",
                          key: "expenseUtilPct",
                          width: 130,
                          render: (_: any, r: any) => {
                            const pct = Math.round(n(r.expenseUtilPct));
                            return (
                              <Progress
                                percent={Math.min(pct, 100)}
                                format={() => `${pct}%`}
                                size="small"
                                strokeColor={pct >= 100 ? "#ff4d4f" : pct >= 70 ? "#faad14" : "#52c41a"}
                              />
                            );
                          },
                        },
                        {
                          title: "Status",
                          key: "expenseStatus",
                          width: 120,
                          render: (_: any, r: any) => {
                            const s = r.expenseStatus || "";
                            const color =
                              s === "On Track" ? "green" :
                              s === "Near Budget" ? "orange" :
                              s === "Over Budget" ? "red" :
                              s === "No Activity" ? "default" :
                              s ? "blue" : "default";
                            return s ? <Tag color={color}>{s}</Tag> : null;
                          },
                        },
                      ]}
                      dataSource={expenseData}
                      pagination={false}
                      size="small"
                      scroll={{ x: 930 }}
                    />
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Row 8: Contractor Cost Performance */}
            <Row className="g-gs mb-3">
              <Col lg="12">
                <Card className="card-bordered p-3">
                  <h6 className="mb-2">Contractor Cost Performance</h6>
                  <div style={{ maxHeight: 300, overflowY: "auto" }}>
                    <AccordionTable
                      columns={[
                        { title: "Contractor", dataIndex: "contractor", key: "contractor", width: 180 },
                        { title: "Budget", dataIndex: "budget", key: "budget", width: 130, render: (v: any) => formatCr(n(v)) },
                        { title: "Actual", dataIndex: "actual", key: "actual", width: 130, render: (v: any) => formatCr(n(v)) },
                        {
                          title: "Variance",
                          dataIndex: "variance",
                          key: "variance",
                          width: 150,
                          render: (v: any, r: any) => {
                            const val = n(v);
                            return (
                              <span style={{ color: val >= 0 ? "green" : "red", fontWeight: 600 }}>
                                {val >= 0 ? "+" : ""}{formatCr(val)} {r.varianceType}
                              </span>
                            );
                          },
                        },
                        {
                          title: "Performance",
                          key: "performance",
                          width: 130,
                          render: (_: any, r: any) => (
                            <Tag color={n(r.variance) >= 0 ? "green" : "red"}>
                              {n(r.variance) >= 0 ? "Under Budget" : "Over Budget"}
                            </Tag>
                          ),
                        },
                      ]}
                      dataSource={contractorData}
                      pagination={false}
                      size="small"
                      scroll={{ x: 720 }}
                    />
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Row 9: Budget Variance Analysis + Forecast Cost at Completion */}
            <Row className="g-gs mb-3">
              <Col lg="6">
                <Card className="card-bordered p-3 h-100">
                  <h6 className="mb-2">Budget Variance Analysis</h6>
                  <div style={{ maxHeight: 300, overflowY: "auto" }}>
                    <AccordionTable
                      columns={[
                        {
                          title: "Budget",
                          key: "budget",
                          width: 130,
                          render: (_: any, r: any) => formatCr(n(r.totalBudget ?? r.budget)),
                        },
                        {
                          title: "Forecast",
                          key: "forecast",
                          width: 130,
                          render: (_: any, r: any) => formatCr(n(r.forecastAtCompletion ?? r.forecast)),
                        },
                        {
                          title: "Variance",
                          key: "variance",
                          width: 150,
                          render: (_: any, r: any) => {
                            const val = n(r.Variance ?? r.variance);
                            const pct = r.variancePct != null ? Number(r.variancePct).toFixed(2) : null;
                            return (
                              <span style={{ color: val >= 0 ? "green" : "red", fontWeight: 600 }}>
                                {val >= 0 ? "+" : ""}{formatCr(val)}{pct != null ? ` (${pct}%)` : ""}
                              </span>
                            );
                          },
                        },
                        {
                          title: "Status",
                          key: "varianceStatus",
                          width: 110,
                          render: (_: any, r: any) => {
                            const s = r.varianceStatus || "";
                            const color = s === "Healthy" ? "green" : s === "Monitor" ? "orange" : s ? "red" : "default";
                            return s ? <Tag color={color}>{s}</Tag> : null;
                          },
                        },
                      ]}
                      dataSource={varianceApiData ? [{ ...varianceApiData, key: 0 }] : []}
                      pagination={false}
                      size="small"
                      scroll={{ x: 520 }}
                    />
                  </div>
                </Card>
              </Col>

              <Col lg="6">
                <Card className="card-bordered p-3 h-100">
                  <h6 className="mb-2">Forecast Cost at Completion</h6>
                  <div style={{ maxHeight: 300, overflowY: "auto" }}>
                    <AccordionTable
                      columns={[
                        {
                          title: "Budget",
                          key: "budget",
                          width: 110,
                          render: (_: any, r: any) => formatCr(n(r.totalBudget ?? r.budget)),
                        },
                        {
                          title: "Committed",
                          key: "committed",
                          width: 110,
                          render: (_: any, r: any) => formatCr(n(r.totalCommitted ?? r.committed)),
                        },
                        {
                          title: "Consumed",
                          key: "consumed",
                          width: 110,
                          render: (_: any, r: any) => formatCr(n(r.totalConsumed ?? r.consumed)),
                        },
                        {
                          title: "Available",
                          key: "available",
                          width: 110,
                          render: (_: any, r: any) => {
                            const val = n(r.availableBudget ?? r.available);
                            return <span style={{ color: val >= 0 ? "#52c41a" : "#ff4d4f" }}>{formatCr(val)}</span>;
                          },
                        },
                        {
                          title: "Forecast",
                          key: "forecastAtCompletion",
                          width: 110,
                          render: (_: any, r: any) => formatCr(n(r.forecastAtCompletion)),
                        },
                        {
                          title: "Variance",
                          key: "variance",
                          width: 140,
                          render: (_: any, r: any) => {
                            const val = n(r.Variance ?? r.variance);
                            const pct = r.variancePct != null ? Number(r.variancePct).toFixed(2) : null;
                            return (
                              <span style={{ color: val >= 0 ? "green" : "red" }}>
                                {val >= 0 ? "+" : ""}{formatCr(val)}{pct != null ? ` (${pct}%)` : ""}
                              </span>
                            );
                          },
                        },
                        {
                          title: "Status",
                          key: "varianceStatus",
                          width: 110,
                          render: (_: any, r: any) => {
                            const s = r.varianceStatus || "";
                            const color = s === "Healthy" ? "green" : s === "Monitor" ? "orange" : s ? "red" : "default";
                            return s ? <Tag color={color}>{s}</Tag> : null;
                          },
                        },
                      ]}
                      dataSource={forecastApiData ? [{ ...forecastApiData, key: 0 }] : []}
                      pagination={false}
                      size="small"
                      scroll={{ x: 720 }}
                    />
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Row 10: Budget Control Alerts */}
            <Row className="g-gs mb-3">
              <Col lg="12">
                <Card className="card-bordered p-3">
                  <h6 className="mb-2">
                    <AlertOutlined style={{ color: "#ff4d4f", marginRight: 8 }} />
                    Budget Control Alerts
                  </h6>
                  <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                    {alertsData.map((alert: any) => {
                      const severity = String(alert.alertSeverity ?? alert.severity ?? "").toLowerCase();
                      const isError = severity === "critical" || severity === "error" || severity === "high";
                      const type = alert.alertType ?? alert.type ?? "";
                      const message = alert.alertMessage ?? alert.message ?? "";
                      return (
                        <Alert
                          key={alert.key}
                          message={
                            <span>
                              <Tag color={isError ? "red" : "orange"}>{type}</Tag>
                              {message}
                            </span>
                          }
                          type={isError ? "error" : "warning"}
                          showIcon
                          style={{ marginBottom: 8 }}
                        />
                      );
                    })}
                  </div>
                </Card>
              </Col>
            </Row>
      </div>
    </Spin>
  );

  const renderReports = () => (
    <Spin spinning={reportLoading}>
      <div>
        <Tabs activeKey={activeReportTab} onChange={setActiveReportTab} tabPosition="top" type="card">
          {/* Report 1: Budget vs Actual (JCR) — static BOQ activity-wise report */}
          <Tabs.TabPane tab="Budget vs Actual" key="report1">
            <BudgetVsActualReport />
          </Tabs.TabPane>

          {/* Report 2: Budget Consumption by Category */}
          <Tabs.TabPane tab="By Category" key="report2">
            <Card className="card-bordered p-3 mt-2">
              <h6 className="mb-1">Budget Consumption by Category</h6>
              <p style={{ color: "#8094ae", fontSize: "12px" }}>
                Which categories are consuming the budget the most. Click (+) to drill down: Category → Budget Line → Transaction → Document
              </p>
              <AccordionTable
                columns={[
                  { title: "Category", key: "category", width: 180, render: (_: any, r: any) => r.budgetCategory || r.category || "" },
                  { title: "Budget", key: "budget", width: 130, render: (_: any, r: any) => formatCr(n(r.totalBudget ?? r.budget)) },
                  { title: "Committed", key: "committed", width: 130, render: (_: any, r: any) => formatCr(n(r.totalCommitted ?? r.committed)) },
                  { title: "Consumed", key: "consumed", width: 130, render: (_: any, r: any) => formatCr(n(r.totalConsumed ?? r.consumed)) },
                  {
                    title: "Available", key: "available", width: 130,
                    render: (_: any, r: any) => {
                      const val = n(r.availableBudget ?? r.available);
                      return <span style={{ color: val >= 0 ? "#52c41a" : "#ff4d4f" }}>{formatCr(val)}</span>;
                    },
                  },
                  {
                    title: "Consumed %", key: "consumedPct", width: 150,
                    render: (_: any, r: any) => {
                      const pct = r.consumedPct != null
                        ? Math.round(n(r.consumedPct))
                        : (() => {
                            const b = n(r.totalBudget ?? r.budget);
                            return b > 0 ? Math.round((n(r.totalConsumed ?? r.consumed) / b) * 100) : 0;
                          })();
                      return (
                        <Progress
                          percent={Math.min(pct, 100)}
                          format={() => `${pct}%`}
                          size="small"
                          strokeColor={pct >= 90 ? "#ff4d4f" : pct >= 70 ? "#faad14" : "#52c41a"}
                        />
                      );
                    },
                  },
                ]}
                dataSource={reportData}
                pagination={reportTablePagination}
                size="small"
                scroll={{ x: 900 }}
                expandable={{
                  expandedRowRender: (record: any) => {
                    const lines = drilldownBudgetLines[record.category] || [];
                    return (
                      <div style={{ padding: "8px 0" }}>
                        <strong style={{ fontSize: "12px", color: "#1890ff", marginBottom: 8, display: "block" }}>
                          Budget Lines under {record.category}
                        </strong>
                        <AccordionTable
                          columns={[
                            { title: "Budget Line", dataIndex: "budgetLine", key: "budgetLine", width: 160 },
                            { title: "Contractor", dataIndex: "contractor", key: "contractor", width: 160 },
                            { title: "Budget", dataIndex: "budget", key: "budget", width: 120, render: (v: number) => formatCr(v) },
                            { title: "Committed", dataIndex: "committed", key: "committed", width: 120, render: (v: number) => formatCr(v) },
                            { title: "Consumed", dataIndex: "consumed", key: "consumed", width: 120, render: (v: number) => formatCr(v) },
                            { title: "Available", dataIndex: "available", key: "available", width: 120, render: (v: number) => <span style={{ color: "#52c41a" }}>{formatCr(v)}</span> },
                          ]}
                          dataSource={lines}
                          pagination={false}
                          size="small"
                          scroll={{ x: "max-content" }}
                          expandable={{
                            expandedRowRender: (lineRecord: any) => {
                              const txns = drilldownTransactions[lineRecord.budgetLine] || [];
                              return (
                                <div style={{ padding: "8px 0" }}>
                                  <strong style={{ fontSize: "12px", color: "#722ed1", marginBottom: 8, display: "block" }}>
                                    Transactions for {lineRecord.budgetLine}
                                  </strong>
                                  <AccordionTable
                                    columns={[
                                      { title: "Txn ID", dataIndex: "txnId", key: "txnId", width: 120 },
                                      { title: "Type", dataIndex: "type", key: "type", width: 130, render: (s: string) => <Tag color={s === "Purchase Order" ? "blue" : s === "Work Order" ? "purple" : "orange"}>{s}</Tag> },
                                      { title: "Vendor", dataIndex: "vendor", key: "vendor", width: 160 },
                                      { title: "Amount", dataIndex: "amount", key: "amount", width: 120, render: (v: number) => formatCr(v) },
                                      { title: "Date", dataIndex: "date", key: "date", width: 120 },
                                      { title: "Status", dataIndex: "status", key: "status", width: 110, render: (s: string) => <Tag color={s === "Completed" ? "green" : s === "In Progress" ? "blue" : "orange"}>{s}</Tag> },
                                    ]}
                                    dataSource={txns}
                                    pagination={false}
                                    size="small"
                                    scroll={{ x: "max-content" }}
                                    expandable={{
                                      expandedRowRender: (txnRecord: any) => {
                                        const docs = drilldownDocuments[txnRecord.txnId] || [];
                                        return (
                                          <div style={{ padding: "8px 0" }}>
                                            <strong style={{ fontSize: "12px", color: "#fa541c", marginBottom: 8, display: "block" }}>
                                              Documents for {txnRecord.txnId}
                                            </strong>
                                            <AccordionTable
                                              columns={[
                                                { title: "Doc ID", dataIndex: "docId", key: "docId", width: 120 },
                                                { title: "Type", dataIndex: "type", key: "type", width: 130, render: (s: string) => <Tag color={s === "Vendor Invoice" ? "red" : s === "RA Bill" ? "purple" : "cyan"}>{s}</Tag> },
                                                { title: "Amount", dataIndex: "amount", key: "amount", width: 120, render: (v: number) => formatCr(v) },
                                                { title: "Date", dataIndex: "date", key: "date", width: 120 },
                                                { title: "Status", dataIndex: "status", key: "status", width: 110, render: (s: string) => <Tag color={s === "Paid" ? "green" : s === "Received" ? "blue" : "orange"}>{s}</Tag> },
                                              ]}
                                              dataSource={docs}
                                              pagination={false}
                                              size="small"
                                              scroll={{ x: "max-content" }}
                                            />
                                          </div>
                                        );
                                      },
                                      rowExpandable: (txnRecord: any) => !!drilldownDocuments[txnRecord.txnId],
                                    }}
                                  />
                                </div>
                              );
                            },
                            rowExpandable: (lineRecord: any) => !!drilldownTransactions[lineRecord.budgetLine],
                          }}
                        />
                      </div>
                    );
                  },
                  rowExpandable: (record: any) => !!drilldownBudgetLines[record.category],
                }}
              />
            </Card>
          </Tabs.TabPane>

          {/* Report 3: Budget Consumption by Contractor */}
          <Tabs.TabPane tab="By Contractor" key="report3">
            <Card className="card-bordered p-3 mt-2">
              <h6 className="mb-1">Budget Consumption by Contractor</h6>
              <p style={{ color: "#8094ae", fontSize: "12px" }}>
                Helps Contracts Manager evaluate contractor performance. Click (+) to drill down: Contractor → Work Orders → Bills → Payment Documents
              </p>
              <AccordionTable
                columns={[
                  { title: "Contractor", dataIndex: "contractor", key: "contractor", width: 180 },
                  { title: "Budget Allocated", dataIndex: "budgetAllocated", key: "budgetAllocated", width: 140, render: (v: number) => formatCr(v) },
                  { title: "Work Order Value", dataIndex: "woValue", key: "woValue", width: 140, render: (v: number) => formatCr(v) },
                  { title: "Bills Submitted", dataIndex: "billsSubmitted", key: "billsSubmitted", width: 140, render: (v: number) => formatCr(v) },
                  { title: "Paid Amount", dataIndex: "paidAmount", key: "paidAmount", width: 130, render: (v: number) => formatCr(v) },
                  { title: "Remaining Budget", dataIndex: "remainingBudget", key: "remainingBudget", width: 140, render: (v: number) => <span style={{ color: "#52c41a" }}>{formatCr(v)}</span> },
                ]}
                dataSource={reportData}
                pagination={reportTablePagination}
                size="small"
                scroll={{ x: 870 }}
                expandable={{
                  expandedRowRender: (record: any) => {
                    const wos = drilldownContractorWOs[record.contractor] || [];
                    return (
                      <div style={{ padding: "8px 0" }}>
                        <strong style={{ fontSize: "12px", color: "#1890ff", marginBottom: 8, display: "block" }}>
                          Work Orders for {record.contractor}
                        </strong>
                        <AccordionTable
                          columns={[
                            { title: "WO ID", dataIndex: "woId", key: "woId", width: 120 },
                            { title: "Work Package", dataIndex: "workPackage", key: "workPackage", width: 150 },
                            { title: "WO Value", dataIndex: "woValue", key: "woValue", width: 120, render: (v: number) => formatCr(v) },
                            { title: "Bills Submitted", dataIndex: "billsSubmitted", key: "billsSubmitted", width: 130, render: (v: number) => formatCr(v) },
                            { title: "Paid", dataIndex: "paid", key: "paid", width: 120, render: (v: number) => formatCr(v) },
                            { title: "Status", dataIndex: "status", key: "status", width: 110, render: (s: string) => <Tag color={s === "Completed" ? "green" : "blue"}>{s}</Tag> },
                          ]}
                          dataSource={wos}
                          pagination={false}
                          size="small"
                          scroll={{ x: "max-content" }}
                          expandable={{
                            expandedRowRender: (woRecord: any) => {
                              const bills = drilldownWOBills[woRecord.woId] || [];
                              return (
                                <div style={{ padding: "8px 0" }}>
                                  <strong style={{ fontSize: "12px", color: "#722ed1", marginBottom: 8, display: "block" }}>
                                    Bills for {woRecord.woId}
                                  </strong>
                                  <AccordionTable
                                    columns={[
                                      { title: "Bill ID", dataIndex: "billId", key: "billId", width: 120 },
                                      { title: "Bill Date", dataIndex: "billDate", key: "billDate", width: 120 },
                                      { title: "Amount", dataIndex: "amount", key: "amount", width: 120, render: (v: number) => formatCr(v) },
                                      { title: "Status", dataIndex: "status", key: "status", width: 110, render: (s: string) => <Tag color={s === "Paid" ? "green" : s === "Approved" ? "blue" : "orange"}>{s}</Tag> },
                                    ]}
                                    dataSource={bills}
                                    pagination={false}
                                    size="small"
                                    scroll={{ x: "max-content" }}
                                    expandable={{
                                      expandedRowRender: (billRecord: any) => {
                                        const docs = drilldownBillDocuments[billRecord.billId] || [];
                                        return (
                                          <div style={{ padding: "8px 0" }}>
                                            <strong style={{ fontSize: "12px", color: "#fa541c", marginBottom: 8, display: "block" }}>
                                              Documents for {billRecord.billId}
                                            </strong>
                                            <AccordionTable
                                              columns={[
                                                { title: "Doc ID", dataIndex: "docId", key: "docId", width: 120 },
                                                { title: "Type", dataIndex: "type", key: "type", width: 140, render: (s: string) => <Tag color={s === "Payment Voucher" ? "green" : "orange"}>{s}</Tag> },
                                                { title: "Amount", dataIndex: "amount", key: "amount", width: 120, render: (v: number) => formatCr(v) },
                                                { title: "Date", dataIndex: "date", key: "date", width: 120 },
                                                { title: "Ref No", dataIndex: "refNo", key: "refNo", width: 130 },
                                                { title: "Status", dataIndex: "status", key: "status", width: 100, render: (s: string) => <Tag color={s === "Paid" ? "green" : s === "Applied" ? "cyan" : "orange"}>{s}</Tag> },
                                              ]}
                                              dataSource={docs}
                                              pagination={false}
                                              size="small"
                                              scroll={{ x: "max-content" }}
                                            />
                                          </div>
                                        );
                                      },
                                      rowExpandable: (billRecord: any) => !!drilldownBillDocuments[billRecord.billId],
                                    }}
                                  />
                                </div>
                              );
                            },
                            rowExpandable: (woRecord: any) => !!drilldownWOBills[woRecord.woId],
                          }}
                        />
                      </div>
                    );
                  },
                  rowExpandable: (record: any) => !!drilldownContractorWOs[record.contractor],
                }}
              />
            </Card>
          </Tabs.TabPane>

          {/* Report 4: Budget vs Purchase Orders */}
          <Tabs.TabPane tab="Budget vs PO" key="report4">
            <Card className="card-bordered p-3 mt-2">
              <h6 className="mb-1">Budget vs Purchase Orders</h6>
              <p style={{ color: "#8094ae", fontSize: "12px" }}>
                Procurement teams track material budgets vs purchase commitments. Click (+) to drill down: Budget Line → PR → PO → GRN → Invoice
              </p>
              <AccordionTable
                columns={[
                  { title: "Budget Line", key: "budgetLine", width: 180, ellipsis: true, render: (_: any, r: any) => r.budgetLineName || r.budgetLine || "" },
                  { title: "Budget", key: "budget", width: 130, render: (_: any, r: any) => formatCr(n(r.budgetValue ?? r.budget)) },
                  { title: "PR Value", key: "prValue", width: 120, render: (_: any, r: any) => formatCr(n(r.prValue)) },
                  { title: "PO Value", key: "poValue", width: 120, render: (_: any, r: any) => formatCr(n(r.poValue)) },
                  { title: "GRN Value", key: "grnValue", width: 120, render: (_: any, r: any) => formatCr(n(r.grnValue)) },
                  { title: "Invoice Value", key: "invoiceValue", width: 130, render: (_: any, r: any) => formatCr(n(r.invoiceValue)) },
                  {
                    title: "Remaining", key: "remaining", width: 130,
                    render: (_: any, r: any) => {
                      const val = n(r.remaining);
                      return <span style={{ color: val >= 0 ? "#52c41a" : "#ff4d4f" }}>{formatCr(val)}</span>;
                    },
                  },
                  {
                    title: "Status", key: "trafficLight", width: 120,
                    render: (_: any, r: any) => {
                      const s = r.trafficLight || "";
                      const color =
                        s === "Healthy" ? "green" :
                        s === "Monitor" ? "orange" :
                        s === "Risk" ? "red" :
                        s === "No Activity" ? "default" :
                        s ? "blue" : "default";
                      return s ? <Tag color={color}>{s}</Tag> : null;
                    },
                  },
                ]}
                dataSource={reportData}
                pagination={reportTablePagination}
                size="small"
                scroll={{ x: 1050 }}
                expandable={{
                  expandedRowRender: (record: any) => {
                    const prs = drilldownBudgetLinePRs[record.budgetLine] || [];
                    return (
                      <div style={{ padding: "8px 0" }}>
                        <strong style={{ fontSize: "12px", color: "#1890ff", marginBottom: 8, display: "block" }}>
                          Purchase Requests for {record.budgetLine}
                        </strong>
                        <AccordionTable
                          columns={[
                            { title: "PR ID", dataIndex: "prId", key: "prId", width: 110 },
                            { title: "PR Date", dataIndex: "prDate", key: "prDate", width: 110 },
                            { title: "PR Amount", dataIndex: "amount", key: "amount", width: 120, render: (v: number) => formatCr(v) },
                            { title: "PO ID", dataIndex: "poId", key: "poId", width: 110 },
                            { title: "PO Value", dataIndex: "poValue", key: "poValue", width: 120, render: (v: number) => formatCr(v) },
                            { title: "GRN Value", dataIndex: "grnValue", key: "grnValue", width: 120, render: (v: number) => formatCr(v) },
                            { title: "Invoice Value", dataIndex: "invoiceValue", key: "invoiceValue", width: 120, render: (v: number) => formatCr(v) },
                            { title: "Status", dataIndex: "status", key: "status", width: 110, render: (s: string) => <Tag color={s === "Completed" ? "green" : "blue"}>{s}</Tag> },
                          ]}
                          dataSource={prs}
                          pagination={false}
                          size="small"
                          scroll={{ x: "max-content" }}
                          expandable={{
                            expandedRowRender: (prRecord: any) => {
                              const docs = drilldownPRDocuments[prRecord.prId] || [];
                              return (
                                <div style={{ padding: "8px 0" }}>
                                  <strong style={{ fontSize: "12px", color: "#722ed1", marginBottom: 8, display: "block" }}>
                                    PO → GRN → Invoice for {prRecord.prId}
                                  </strong>
                                  <AccordionTable
                                    columns={[
                                      { title: "Doc ID", dataIndex: "docId", key: "docId", width: 120 },
                                      { title: "Type", dataIndex: "type", key: "type", width: 140, render: (s: string) => <Tag color={s === "Purchase Order" ? "blue" : s === "Goods Receipt" ? "cyan" : "green"}>{s}</Tag> },
                                      { title: "Vendor", dataIndex: "vendor", key: "vendor", width: 150 },
                                      { title: "Amount", dataIndex: "amount", key: "amount", width: 120, render: (v: number) => formatCr(v) },
                                      { title: "Date", dataIndex: "date", key: "date", width: 120 },
                                      { title: "Status", dataIndex: "status", key: "status", width: 110, render: (s: string) => <Tag color={s === "Paid" ? "green" : s === "Received" ? "cyan" : s === "Completed" ? "green" : "orange"}>{s}</Tag> },
                                    ]}
                                    dataSource={docs}
                                    pagination={false}
                                    size="small"
                                    scroll={{ x: "max-content" }}
                                  />
                                </div>
                              );
                            },
                            rowExpandable: (prRecord: any) => !!drilldownPRDocuments[prRecord.prId],
                          }}
                        />
                      </div>
                    );
                  },
                  rowExpandable: (record: any) => !!drilldownBudgetLinePRs[record.budgetLine],
                }}
              />
            </Card>
          </Tabs.TabPane>

          {/* Report 5: Budget vs Expenses */}
          <Tabs.TabPane tab="Budget vs Expenses" key="report5">
            <Card className="card-bordered p-3 mt-2">
              <h6 className="mb-1">Budget vs Expenses</h6>
              <p style={{ color: "#8094ae", fontSize: "12px" }}>
                Tracks petty cash and operational expenses against budget. Click (+) to drill down: Expense Category → Expense Entries → Voucher Documents
              </p>
              <AccordionTable
                columns={[
                  { title: "Expense Category", dataIndex: "expenseCategory", key: "expenseCategory", width: 160, render: (v: string) => v ?? "—" },
                  { title: "Budget", dataIndex: "expenseLineBudget", key: "expenseLineBudget", width: 120, render: (v: number) => formatCr(Number(v)) },
                  { title: "Expense Requests", dataIndex: "expenseRequested", key: "expenseRequested", width: 140, render: (v: number) => formatCr(Number(v)) },
                  { title: "Approved", dataIndex: "expenseApproved", key: "expenseApproved", width: 120, render: (v: number) => formatCr(Number(v)) },
                  { title: "Paid", dataIndex: "paidAmount", key: "paidAmount", width: 120, render: (v: number) => formatCr(Number(v)) },
                  { title: "Balance", dataIndex: "expenseAvailable", key: "expenseAvailable", width: 120, render: (v: number) => <span style={{ color: "#52c41a" }}>{formatCr(Number(v))}</span> },
                ]}
                dataSource={reportData}
                pagination={reportTablePagination}
                size="small"
                scroll={{ x: 780 }}
                expandable={{
                  expandedRowRender: (record: any) => {
                    const entries = drilldownExpenseEntries[record.expenseCategory] || [];
                    return (
                      <div style={{ padding: "8px 0" }}>
                        <strong style={{ fontSize: "12px", color: "#1890ff", marginBottom: 8, display: "block" }}>
                          Expense Entries for {record.expenseCategory}
                        </strong>
                        <AccordionTable
                          columns={[
                            { title: "Expense ID", dataIndex: "expId", key: "expId", width: 110 },
                            { title: "Date", dataIndex: "date", key: "date", width: 110 },
                            { title: "Description", dataIndex: "description", key: "description", width: 180 },
                            { title: "Amount", dataIndex: "amount", key: "amount", width: 120, render: (v: number) => formatCr(v) },
                            { title: "Approved By", dataIndex: "approvedBy", key: "approvedBy", width: 120 },
                            { title: "Status", dataIndex: "status", key: "status", width: 100, render: (s: string) => <Tag color={s === "Paid" ? "green" : "blue"}>{s}</Tag> },
                          ]}
                          dataSource={entries}
                          pagination={false}
                          size="small"
                          scroll={{ x: "max-content" }}
                          expandable={{
                            expandedRowRender: (entryRecord: any) => {
                              const docs = drilldownExpenseDocuments[entryRecord.expId] || [];
                              return (
                                <div style={{ padding: "8px 0" }}>
                                  <strong style={{ fontSize: "12px", color: "#722ed1", marginBottom: 8, display: "block" }}>
                                    Voucher for {entryRecord.expId}
                                  </strong>
                                  <AccordionTable
                                    columns={[
                                      { title: "Voucher ID", dataIndex: "docId", key: "docId", width: 130 },
                                      { title: "Type", dataIndex: "type", key: "type", width: 140, render: (s: string) => <Tag color="purple">{s}</Tag> },
                                      { title: "Amount", dataIndex: "amount", key: "amount", width: 120, render: (v: number) => formatCr(v) },
                                      { title: "Date", dataIndex: "date", key: "date", width: 120 },
                                      { title: "Payment Mode", dataIndex: "paymentMode", key: "paymentMode", width: 130 },
                                      { title: "Status", dataIndex: "status", key: "status", width: 100, render: (s: string) => <Tag color={s === "Paid" ? "green" : "blue"}>{s}</Tag> },
                                    ]}
                                    dataSource={docs}
                                    pagination={false}
                                    size="small"
                                    scroll={{ x: "max-content" }}
                                  />
                                </div>
                              );
                            },
                            rowExpandable: (entryRecord: any) => !!drilldownExpenseDocuments[entryRecord.expId],
                          }}
                        />
                      </div>
                    );
                  },
                  rowExpandable: (record: any) => !!drilldownExpenseEntries[record.expenseCategory],
                }}
              />
            </Card>
          </Tabs.TabPane>

          {/* Report 6: Budget vs Work Orders */}
          <Tabs.TabPane tab="Budget vs WO" key="report6">
            <Card className="card-bordered p-3 mt-2">
              <h6 className="mb-1">Budget vs Work Orders</h6>
              <p style={{ color: "#8094ae", fontSize: "12px" }}>
                Tracks contractor work package budgets vs execution. Click (+) to drill down: Work Package → Work Order → Bills → Payment Documents
              </p>
              <AccordionTable
                columns={[
                  { title: "Work Package", dataIndex: "workPackage", key: "workPackage", width: 150 },
                  { title: "Contractor", dataIndex: "contractor", key: "contractor", width: 120 },
                  { title: "Budget", dataIndex: "budget", key: "budget", width: 120, render: (v: number) => formatCr(v) },
                  { title: "WO Value", dataIndex: "woValue", key: "woValue", width: 120, render: (v: number) => formatCr(v) },
                  { title: "Bills Submitted", dataIndex: "billsSubmitted", key: "billsSubmitted", width: 130, render: (v: number) => formatCr(v) },
                  { title: "Paid", dataIndex: "paid", key: "paid", width: 120, render: (v: number) => formatCr(v) },
                  { title: "Balance", dataIndex: "balance", key: "balance", width: 120, render: (v: number) => <span style={{ color: "#52c41a" }}>{formatCr(v)}</span> },
                ]}
                dataSource={reportData}
                pagination={reportTablePagination}
                size="small"
                scroll={{ x: 880 }}
                expandable={{
                  expandedRowRender: (record: any) => {
                    const wos = drilldownWorkPackageWOs[record.workPackage] || [];
                    return (
                      <div style={{ padding: "8px 0" }}>
                        <strong style={{ fontSize: "12px", color: "#1890ff", marginBottom: 8, display: "block" }}>
                          Work Orders for {record.workPackage}
                        </strong>
                        <AccordionTable
                          columns={[
                            { title: "WO ID", dataIndex: "woId", key: "woId", width: 120 },
                            { title: "Contractor", dataIndex: "contractor", key: "contractor", width: 120 },
                            { title: "WO Value", dataIndex: "woValue", key: "woValue", width: 120, render: (v: number) => formatCr(v) },
                            { title: "RA Bills", dataIndex: "raBills", key: "raBills", width: 120, render: (v: number) => formatCr(v) },
                            { title: "Paid", dataIndex: "paid", key: "paid", width: 120, render: (v: number) => formatCr(v) },
                            { title: "Status", dataIndex: "status", key: "status", width: 110, render: (s: string) => <Tag color={s === "Completed" ? "green" : "blue"}>{s}</Tag> },
                          ]}
                          dataSource={wos}
                          pagination={false}
                          size="small"
                          scroll={{ x: "max-content" }}
                          expandable={{
                            expandedRowRender: (woRecord: any) => {
                              const bills = drilldownWOBills[woRecord.woId] || [];
                              return (
                                <div style={{ padding: "8px 0" }}>
                                  <strong style={{ fontSize: "12px", color: "#722ed1", marginBottom: 8, display: "block" }}>
                                    Bills for {woRecord.woId}
                                  </strong>
                                  <AccordionTable
                                    columns={[
                                      { title: "Bill ID", dataIndex: "billId", key: "billId", width: 120 },
                                      { title: "Bill Date", dataIndex: "billDate", key: "billDate", width: 120 },
                                      { title: "Amount", dataIndex: "amount", key: "amount", width: 120, render: (v: number) => formatCr(v) },
                                      { title: "Status", dataIndex: "status", key: "status", width: 110, render: (s: string) => <Tag color={s === "Paid" ? "green" : s === "Approved" ? "blue" : "orange"}>{s}</Tag> },
                                    ]}
                                    dataSource={bills}
                                    pagination={false}
                                    size="small"
                                    scroll={{ x: "max-content" }}
                                    expandable={{
                                      expandedRowRender: (billRecord: any) => {
                                        const docs = drilldownBillDocuments[billRecord.billId] || [];
                                        return (
                                          <div style={{ padding: "8px 0" }}>
                                            <strong style={{ fontSize: "12px", color: "#fa541c", marginBottom: 8, display: "block" }}>
                                              Documents for {billRecord.billId}
                                            </strong>
                                            <AccordionTable
                                              columns={[
                                                { title: "Doc ID", dataIndex: "docId", key: "docId", width: 120 },
                                                { title: "Type", dataIndex: "type", key: "type", width: 140, render: (s: string) => <Tag color={s === "Payment Voucher" ? "green" : "orange"}>{s}</Tag> },
                                                { title: "Amount", dataIndex: "amount", key: "amount", width: 120, render: (v: number) => formatCr(v) },
                                                { title: "Date", dataIndex: "date", key: "date", width: 120 },
                                                { title: "Ref No", dataIndex: "refNo", key: "refNo", width: 130 },
                                                { title: "Status", dataIndex: "status", key: "status", width: 100, render: (s: string) => <Tag color={s === "Paid" ? "green" : s === "Applied" ? "cyan" : "orange"}>{s}</Tag> },
                                              ]}
                                              dataSource={docs}
                                              pagination={false}
                                              size="small"
                                              scroll={{ x: "max-content" }}
                                            />
                                          </div>
                                        );
                                      },
                                      rowExpandable: (billRecord: any) => !!drilldownBillDocuments[billRecord.billId],
                                    }}
                                  />
                                </div>
                              );
                            },
                            rowExpandable: (woRecord: any) => !!drilldownWOBills[woRecord.woId],
                          }}
                        />
                      </div>
                    );
                  },
                  rowExpandable: (record: any) => !!drilldownWorkPackageWOs[record.workPackage],
                }}
              />
            </Card>
          </Tabs.TabPane>

          {/* Report 7: Budget Utilization Trend */}
          <Tabs.TabPane tab="Utilization Trend" key="report7">
            <Card className="card-bordered p-3 mt-2">
              <h6 className="mb-1">Budget Utilization Trend</h6>
              <p style={{ color: "#8094ae", fontSize: "12px" }}>
                How budget is being spent over time. Click (+) to drill down: Month → Transactions → Documents
              </p>
              <div ref={utilizationTrendChartRef} style={{ width: "100%", height: "350px" }} />
              <AccordionTable
                columns={[
                  {
                    title: "Month", key: "month", width: 180,
                    render: (_: any, r: any) => {
                      const name = r.fullMonthName || r.monthName || r.month || "";
                      return r.year ? `${name} ${r.year}` : name;
                    },
                  },
                  { title: "Budget Consumed", key: "consumedValue", width: 180, render: (_: any, r: any) => formatCr(n(r.consumedValue ?? r.budgetConsumed)) },
                  { title: "Cumulative", key: "cumulativeValue", width: 180, render: (_: any, r: any) => formatCr(n(r.cumulativeValue)) },
                ]}
                dataSource={reportData}
                pagination={reportTablePagination}
                size="small"
                scroll={{ x: "max-content" }}
                style={{ marginTop: 16 }}
                expandable={{
                  expandedRowRender: (record: any) => {
                    const txns = drilldownMonthTransactions[record.month] || [];
                    return (
                      <div style={{ padding: "8px 0" }}>
                        <strong style={{ fontSize: "12px", color: "#1890ff", marginBottom: 8, display: "block" }}>
                          Transactions in {record.month}
                        </strong>
                        <AccordionTable
                          columns={[
                            { title: "Txn ID", dataIndex: "txnId", key: "txnId", width: 120 },
                            { title: "Type", dataIndex: "type", key: "type", width: 130, render: (s: string) => <Tag color={s === "Purchase Order" ? "blue" : s === "Work Order" ? "purple" : s === "RA Bill" ? "cyan" : "orange"}>{s}</Tag> },
                            { title: "Description", dataIndex: "description", key: "description", width: 200 },
                            { title: "Amount", dataIndex: "amount", key: "amount", width: 120, render: (v: number) => formatCr(v) },
                            { title: "Date", dataIndex: "date", key: "date", width: 120 },
                          ]}
                          dataSource={txns}
                          pagination={false}
                          size="small"
                          scroll={{ x: "max-content" }}
                          expandable={{
                            expandedRowRender: (txnRecord: any) => {
                              const docs = drilldownMonthTxnDocuments[txnRecord.txnId] || [];
                              return (
                                <div style={{ padding: "8px 0" }}>
                                  <strong style={{ fontSize: "12px", color: "#722ed1", marginBottom: 8, display: "block" }}>
                                    Documents for {txnRecord.txnId}
                                  </strong>
                                  <AccordionTable
                                    columns={[
                                      { title: "Doc ID", dataIndex: "docId", key: "docId", width: 120 },
                                      { title: "Type", dataIndex: "type", key: "type", width: 140, render: (s: string) => <Tag color={s === "Goods Receipt" ? "cyan" : s === "Vendor Invoice" ? "green" : s === "RA Bill" ? "purple" : s === "Payment Voucher" ? "blue" : "orange"}>{s}</Tag> },
                                      { title: "Amount", dataIndex: "amount", key: "amount", width: 120, render: (v: number) => formatCr(v) },
                                      { title: "Date", dataIndex: "date", key: "date", width: 120 },
                                      { title: "Status", dataIndex: "status", key: "status", width: 110, render: (s: string) => <Tag color={s === "Paid" ? "green" : s === "Received" ? "cyan" : s === "Approved" ? "blue" : "orange"}>{s}</Tag> },
                                    ]}
                                    dataSource={docs}
                                    pagination={false}
                                    size="small"
                                    scroll={{ x: "max-content" }}
                                  />
                                </div>
                              );
                            },
                            rowExpandable: (txnRecord: any) => !!drilldownMonthTxnDocuments[txnRecord.txnId],
                          }}
                        />
                      </div>
                    );
                  },
                  rowExpandable: (record: any) => !!drilldownMonthTransactions[record.month],
                }}
              />
            </Card>
          </Tabs.TabPane>

          {/* Report 8: Budget Forecast at Completion */}
          <Tabs.TabPane tab="Forecast" key="report8">
            <Card className="card-bordered p-3 mt-2">
              <h6 className="mb-1">Budget Forecast at Completion</h6>
              <p style={{ color: "#8094ae", fontSize: "12px" }}>
                Predicts final project cost — Used by Project Directors. Click (+) to drill down: Category → Budget Line → Transaction → Document
              </p>
              <AccordionTable
                columns={[
                  { title: "Category", key: "category", width: 180, render: (_: any, r: any) => r.budgetCategory || r.category || "" },
                  { title: "Budget", key: "budget", width: 130, render: (_: any, r: any) => formatCr(n(r.totalBudget ?? r.budget)) },
                  { title: "Committed", key: "committed", width: 130, render: (_: any, r: any) => formatCr(n(r.totalCommitted ?? r.committed)) },
                  { title: "Consumed", key: "consumed", width: 130, render: (_: any, r: any) => formatCr(n(r.totalConsumed ?? r.consumed)) },
                  {
                    title: "Available", key: "available", width: 130,
                    render: (_: any, r: any) => {
                      const val = n(r.availableBudget ?? r.available);
                      return <span style={{ color: val >= 0 ? "#52c41a" : "#ff4d4f" }}>{formatCr(val)}</span>;
                    },
                  },
                  { title: "Forecast", key: "forecast", width: 130, render: (_: any, r: any) => formatCr(n(r.forecastAtCompletion ?? r.forecast)) },
                  {
                    title: "Variance", key: "variance", width: 140,
                    render: (_: any, r: any) => {
                      const val = n(r.budgetVariance ?? r.variance);
                      return (
                        <span style={{ color: val >= 0 ? "green" : "red", fontWeight: 600 }}>
                          {val >= 0 ? "+" : ""}{formatCr(val)}
                        </span>
                      );
                    },
                  },
                  {
                    title: "Status", key: "status", width: 120,
                    render: (_: any, r: any) => {
                      const val = n(r.budgetVariance ?? r.variance);
                      return (
                        <Tag color={val >= 0 ? "green" : "red"}>
                          {val >= 0 ? "On Track" : "At Risk"}
                        </Tag>
                      );
                    },
                  },
                ]}
                dataSource={reportData}
                pagination={reportTablePagination}
                size="small"
                scroll={{ x: 1150 }}
                summary={() => {
                  const totals = reportData.reduce(
                    (acc, r) => ({
                      budget: acc.budget + n(r.totalBudget ?? r.budget),
                      committed: acc.committed + n(r.totalCommitted ?? r.committed),
                      consumed: acc.consumed + n(r.totalConsumed ?? r.consumed),
                      available: acc.available + n(r.availableBudget ?? r.available),
                      forecast: acc.forecast + n(r.forecastAtCompletion ?? r.forecast),
                      variance: acc.variance + n(r.budgetVariance ?? r.variance),
                    }),
                    { budget: 0, committed: 0, consumed: 0, available: 0, forecast: 0, variance: 0 }
                  );
                  return (
                    <Table.Summary.Row style={{ fontWeight: 700, background: "#fafafa" }}>
                      <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>{formatCr(totals.budget)}</Table.Summary.Cell>
                      <Table.Summary.Cell index={2}>{formatCr(totals.committed)}</Table.Summary.Cell>
                      <Table.Summary.Cell index={3}>{formatCr(totals.consumed)}</Table.Summary.Cell>
                      <Table.Summary.Cell index={4}>
                        <span style={{ color: totals.available >= 0 ? "#52c41a" : "#ff4d4f" }}>{formatCr(totals.available)}</span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={5}>{formatCr(totals.forecast)}</Table.Summary.Cell>
                      <Table.Summary.Cell index={6}>
                        <span style={{ color: totals.variance >= 0 ? "green" : "red" }}>
                          {totals.variance >= 0 ? "+" : ""}{formatCr(totals.variance)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={7}>
                        <Tag color={totals.variance >= 0 ? "green" : "red"}>
                          {totals.variance >= 0 ? "On Track" : "At Risk"}
                        </Tag>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
            </Card>
          </Tabs.TabPane>
        </Tabs>
      </div>
    </Spin>
  );

  return (
    <>
      <Head title="Budget Analysis" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h4">
                {projectName ? `${projectName} / ` : ""}Project Budget Control Tower
              </BlockTitle>
              <p style={{ color: "#8094ae", fontSize: "13px" }}>
                Integrated Dashboard for Budget and Expense Analysis
              </p>
            </BlockHeadContent>
            <BlockHeadContent>
              <a
                href="#back"
                onClick={(ev) => {
                  ev.preventDefault();
                  navigate(-1);
                }}
                className="btn btn-outline-light bg-white d-none d-sm-inline-flex"
              >
                <Icon name="arrow-left" />
                <span>Back</span>
              </a>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        {renderFilters()}

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
          items={[
            {
              key: "dashboard",
              label: (
                <span>
                  <BarChartOutlined /> Dashboard
                </span>
              ),
              children: renderDashboard(),
            },
            {
              key: "reports",
              label: (
                <span>
                  <FileTextOutlined /> Reports
                </span>
              ),
              children: renderReports(),
            },
          ]}
        />
      </Content>
    </>
  );
};

export default BudgetAnalysis;
